// The in-memory Work twin (M2-07): spaces, boards, views, fields, tasks, follows, the inbox,
// task threads and the work side of template application (fake-org.ts owns `templates.*`), with
// the API's rules as the M2-01 contracts state them, so Work mode is built on the real shapes
// while M2-03 (boards, tasks) and M2-04 (inbox) build the server. Saved views are evaluated here the way `boards/{id}/tasks?view=`
// will be (view-engine.ts); hard dependencies block started and done statuses; dependencies
// never form a cycle; every task write records its event (`tasks/{id}/history`); `client_id`
// makes task creation idempotent; If-Match guards board and task patches. Nothing here is
// reached by the real API: `fake-work-contract.test.ts` holds it to the contracts.
import {
  BOARD_TEMPLATE_KEYS,
  BOARD_TEMPLATES,
  type BoardTemplateKey,
  type FieldDefLike,
} from "@companyos/domain-work/boards";
import {
  mappingProblems,
  parseTaskRow,
  previewCsv,
  rowIdentity,
  type TaskImportMapping,
} from "@companyos/domain-work/import";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  applyView,
  isDoneStatus,
  type ViewContext,
  validGroupBy,
} from "../features/work/view-engine";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import type { FakeCompany, FakeDe } from "./fake-data";
import { uid } from "./fake-data";
import { seats as seatRows } from "./fake-entry";
import { INBOX_FILTERS, inboxMatches, textMatches } from "./fake-merge";
import {
  BASIC_STATUSES,
  type Board,
  type Channel,
  type FieldDef,
  type InboxItem,
  type SeedMessage,
  type Space,
  type StatusDef,
  type StoredTask,
  seedWork,
  type View,
} from "./fake-work-data";
import type { TemplateSpec } from "./types";

type TaskCard = OperationOutput<"tasks.list">["data"][number];
type TaskDetail = OperationOutput<"tasks.get">;
type Message = OperationOutput<"messages.list">["data"][number];
type Event = OperationOutput<"tasks.history">["data"][number];
type Follow = OperationOutput<"follows.put">;

type WorkState = {
  spaces: Space[];
  channels: Channel[];
  boards: Board[];
  fields: FieldDef[];
  views: View[];
  tasks: StoredTask[];
  inbox: InboxItem[];
  /** thread id → messages, oldest first. */
  messages: Map<string, Message[]>;
  /** thread id → the task it belongs to. */
  threads: Map<string, string>;
  events: Event[];
  follows: Follow[];
  /** `client_id` → task id (idempotent creation). */
  clientIds: Map<string, string>;
};

/** TASK-LOCK: what the work twin reads of a run (the agents twin's `Run`). */
export type LiveRunLike = {
  id?: string;
  membership_id: string;
  created_at: string;
  task_id?: string | null;
  state?: string;
  lease_until?: string | null;
  started_at?: string | null;
};

export type WorkDeps = {
  now: () => Date;
  companies: FakeCompany[];
  desOf: (companyId: string) => FakeDe[];
  /** TASK-LOCK: the company's runs (a running one on a task, its lease live, locks the task). */
  runsOf?: (companyId: string) => readonly LiveRunLike[];
  /** The membership of the company's owner account (the demo inbox's reader), or null. */
  ownerMembership: (companyId: string) => string | null;
  /** Whether an add-on is on for the caller (the twin gates `chat` threads like the API). */
  featureOn: (p: Principal, feature: "chat" | "agents") => boolean;
  /** Where the work module registers its side of `templates.apply` (fake-org.ts calls it). */
  registerTemplateHandlers?: (handlers: FakeWorkTemplateHandlers) => void;
  /** Where the work module hands the chat twin (fake-chat.ts) its spaces, boards and channels. */
  registerBridge?: (bridge: WorkBridge) => void;
  /** SHARED-DES: the `not_in_reach` refusal for assigning a shared DE outside its grants. */
  taskRefusal?: (p: Principal, seatIds: readonly string[]) => Response | null;
};

/**
 * What the chat twin reads of this one: the company's spaces (one array both twins write) and,
 * for a space's sidebar, the boards and the channels this twin holds (board homes, template
 * channels).
 */
export type WorkBridge = {
  spacesOf: (companyId: string) => Space[] | null;
  sidebarOf: (
    companyId: string,
    spaceId: string,
  ) => { boards: Board[]; channels: Channel[] } | null;
  /** A board this twin holds (chat's "make task" files into it). */
  boardOf: (companyId: string, boardId: string) => Board | null;
  /** M7-02: a live task this twin holds (the pages twin sets its `page_id`). */
  taskOf: (companyId: string, taskId: string) => StoredTask | null;
  /** M7-02: a page's thread, so `messages.*` serve it like a task's. */
  addThread: (companyId: string, threadId: string, subjectId: string) => void;
  /** CHART-DIMENSIONS: the company's live tasks (the chart's load counts them per seat). */
  tasksOf: (companyId: string) => StoredTask[] | null;
  /** WOS-BUNDLE: the company's work rows, written in place (duplicate copies into them). */
  stateOf: (companyId: string) => Pick<
    WorkState,
    | "spaces"
    | "channels"
    | "boards"
    | "fields"
    | "views"
    | "tasks"
    | "events"
    | "follows"
    // INBOX-READABLE: the local seed's Recall notices (fake.ts `seedMemoryInbox`).
    | "inbox"
  > | null;
  /** TASK-FILES: an event in a task's history (its Activity tab), as the files twin writes it. */
  recordTaskEvent: (
    companyId: string,
    taskId: string,
    type: Event["type"],
    payload: Record<string, unknown>,
    actor: Event["actor"],
  ) => void;
  /** TASK-FILES: the task whose thread holds a comment (message), or null. */
  messageTask: (companyId: string, messageId: string) => string | null;
};

type SpecSpace = NonNullable<TemplateSpec["spaces"]>[number];
type SpecBoard = NonNullable<SpecSpace["boards"]>[number];

/**
 * The work module's side of `templates.apply`, as services/api registers it through
 * `domain/templates/work-hooks.ts` (chat: a space with its channels; boards: a board with its
 * statuses, views and starter tasks). fake-org.ts runs the application and calls these with the
 * resolved spec (placeholders already filled).
 */
export type FakeWorkTemplateHandlers = {
  space: (
    p: Principal,
    input: { templateId: string; space: Omit<SpecSpace, "boards">; teamId: string | null },
  ) => { spaceId: string; channelIds: string[] };
  board: (
    p: Principal,
    input: { templateId: string; spaceId: string; board: SpecBoard },
  ) => { boardId: string; channelIds: string[]; taskIds: string[] } | Response;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY = /^[a-z][a-z0-9_]*$/;
const BOARD_COLORS: readonly string[] = ["orange", "green", "purple", "amber", "blue", "rose"];
const OPENING_KINDS: readonly string[] = [
  "board",
  "list",
  "table",
  "calendar",
  "timeline",
  "gallery",
];
/** BOARD-ORDER: the manual order (lower first), ties by name (the API's order). */
const byPosition = (a: { position: number; name: string }, b: { position: number; name: string }) =>
  a.position - b.position || a.name.localeCompare(b.name);
const forbidden = () => problem(403, "forbidden", "Forbidden");
const NONE_GROUP = "~";
/** M2-02's per-caller channel fields: the demo reader is a member of every channel it sees. */
const SUBSCRIBED = {
  posting_policy: "everyone",
  topic_policy: "everyone",
  broadcast_policy: "everyone",
  default_notify_level: "all",
  joined: true,
  notify_level: "all",
  muted: false,
  following: false,
  // WF-FIX (2026-10-26): a joined channel where everyone posts: the caller may add members.
  can_invite: true,
  // (These rows are not per caller: no remove/manage controls are offered from them.)
  can_manage: false,
  // CHAT-PRESENCE: board channels are never closed DMs.
  closed: false,
} as const;

export function createWorkFake(deps: WorkDeps): Partial<Record<RouteId, Handler>> {
  const states = new Map<string, WorkState>();
  let seq = 0x100;
  let eventSeq = 0;
  const ids = { next: (group: number) => uid(group, ++seq) };
  const stamp = () => deps.now().toISOString();

  function stateOf(company: FakeCompany): WorkState {
    let s = states.get(company.id);
    if (s) return s;
    const des = deps.desOf(company.id);
    const seed = seedWork(company, des, ids, deps.now(), deps.ownerMembership(company.id));
    s = {
      spaces: seed.spaces,
      channels: seed.channels,
      boards: seed.boards,
      fields: seed.fields,
      views: seed.views,
      tasks: seed.tasks,
      inbox: seed.inbox,
      messages: new Map(),
      threads: new Map(),
      events: [],
      follows: [],
      clientIds: new Map(),
    };
    states.set(company.id, s);
    const members = membersOf(company, des);
    for (const t of s.tasks) {
      recordSeed(company, t);
      if (t.thread_id) s.threads.set(t.thread_id, t.id);
      const seeded = seed.messages.get(t.id);
      if (seeded && t.thread_id)
        s.messages.set(
          t.thread_id,
          seeded.map((m) => seedMessage(company, t.thread_id as string, m, members)),
        );
    }
    // The mention points at the thread message that names the owner.
    // INBOX-PREFS: who it is from — the mention's author; the DE an escalation waits for.
    for (const item of s.inbox) {
      if (item.kind === "agent_escalation") {
        item.from_membership_id = members.riley?.membership ?? null;
        continue;
      }
      if (item.kind !== "mention") continue;
      const task = s.tasks.find((t) => t.id === item.subject_id);
      const msgs = task?.thread_id ? (s.messages.get(task.thread_id) ?? []) : [];
      const msg = [...msgs].reverse().find((m) => m.mentions.length);
      item.message_id = msg?.id ?? null;
      item.from_membership_id = msg?.author_membership_id ?? null;
    }
    return s;
  }

  /** Who the seeded thread authors are: the lead's holder, each DE, and the owner. */
  function membersOf(
    company: FakeCompany,
    des: FakeDe[],
  ): Record<
    string,
    { membership: string; kind: "person" | "digital_employee"; seat: string | null }
  > {
    const out: Record<
      string,
      { membership: string; kind: "person" | "digital_employee"; seat: string | null }
    > = {};
    const lead = des[0]?.supervisor;
    if (lead) out.lead = { membership: lead.membership_id, kind: "person", seat: lead.seat_id };
    for (const d of des)
      out[d.display_name.toLowerCase()] = {
        membership: d.membership_id,
        kind: "digital_employee",
        seat: d.seat_id,
      };
    const owner = deps.ownerMembership(company.id);
    if (owner) out.owner = { membership: owner, kind: "person", seat: null };
    return out;
  }

  function seedMessage(
    company: FakeCompany,
    threadId: string,
    m: SeedMessage,
    members: ReturnType<typeof membersOf>,
  ): Message {
    const who = members[m.author];
    const at = new Date(deps.now().getTime() - m.minutesAgo * 60_000).toISOString();
    return messageRow(company.id, threadId, at, m.text, who?.membership ?? null, {
      kind: who?.kind ?? "system",
      seat: who?.seat ?? null,
      mentions: (m.mentions ?? []).flatMap((k) => {
        const x = members[k];
        return x ? [`person:${x.membership}`] : [];
      }),
    });
  }

  function messageRow(
    companyId: string,
    threadId: string,
    at: string,
    text: string,
    membership: string | null,
    o: { kind: Message["actor_kind"]; seat: string | null; mentions: string[]; clientId?: string },
  ): Message {
    return {
      company_id: companyId,
      created_at: at,
      id: ids.next(0x5800),
      thread_id: threadId,
      author_membership_id: membership,
      author_kind:
        membership === null ? "system" : o.kind === "digital_employee" ? "agent" : "person",
      bookmarked: false,
      body: text.split("\n").map((line) => ({ type: "paragraph", content: line })),
      body_text: text,
      mentions: o.mentions,
      card_refs: [],
      parent_message_id: null,
      client_id: o.clientId ?? null,
      edited_at: null,
      deleted_at: null,
      pinned_at: null,
      actor_kind: o.kind,
      actor_membership_id: membership,
      actor_seat_id: o.seat,
      actor_key_id: null,
      actor_run_id: null,
      actor_acting_for_membership_id: null,
      actor_identity_mode: null,
      actor_crew_agent_id: null,
      actor_policy_snapshot_id: null,
      actor_auth_version: null,
      reactions: [],
      agent_wakes: [],
      attachments: [],
    };
  }

  /** wp/ACTIONS: a task thread's message for a write (404 when the thread is not a task's). */
  function taskMessage(
    principal: Principal | null,
    params: Record<string, string | undefined>,
  ): Message | Response {
    if (!principal || !reader(principal)) return notFound();
    if (!deps.featureOn(principal, "chat")) return chatOff();
    const s = stateOf(principal.company);
    const taskId = s.threads.get(params.id ?? "");
    if (!taskId || !taskIn(principal, taskId)) return notFound();
    const m = (s.messages.get(params.id ?? "") ?? []).find((x) => x.id === params.mid);
    if (!m || m.deleted_at) return notFound();
    return m;
  }

  // Principals ---------------------------------------------------------------------------------

  /** COS-MODES: the `task.agent_directed` events already taken back (a second Take back: 409). */
  const takenBack = new Set<string>();

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;

  /** Client viewers hold no relation on the company's spaces: they see no work (05). */
  const reader = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;

  const rowActor = (p: Principal) =>
    p.operator && !membershipOf(p)
      ? { actor_kind: "agency_user" as const, actor_membership_id: null }
      : { actor_kind: "person" as const, actor_membership_id: membershipOf(p) };

  const eventActor = (p: Principal | null): Event["actor"] => {
    if (!p) return { kind: "system" };
    const m = membershipOf(p);
    if (m) return { kind: "person", membership_id: m };
    return { kind: "agency_user", user_id: p.me.user.id, auth_version: 0 };
  };

  function record(
    company: FakeCompany,
    p: Principal | null,
    type: Event["type"],
    taskId: string,
    payload: Record<string, unknown>,
    at = stamp(),
    version = 1,
  ) {
    stateOf(company).events.push({
      id: ids.next(0x5c00),
      company_id: company.id,
      seq: ++eventSeq,
      type,
      version,
      actor: eventActor(p),
      subject: { kind: "task", id: taskId },
      payload,
      correlation_id: null,
      causation_id: null,
      occurred_at: at,
    });
  }

  function recordSeed(company: FakeCompany, t: StoredTask) {
    const s = states.get(company.id);
    if (!s) return;
    s.events.push({
      id: ids.next(0x5c00),
      company_id: company.id,
      seq: ++eventSeq,
      type: "task.created",
      version: 1,
      actor: { kind: "system" },
      subject: { kind: "task", id: t.id },
      payload: {
        board_id: t.board_id,
        number: t.number,
        title: t.title,
        status: t.status,
        priority: t.priority,
        parent_task_id: t.parent_task_id,
        origin: t.origin,
        origin_ref: t.origin_ref,
        assignee_seat_ids: t.assignee_seat_ids,
        due_at: t.due_at,
      },
      correlation_id: null,
      causation_id: null,
      occurred_at: t.created_at,
    });
  }

  // Lookups ------------------------------------------------------------------------------------

  const live = <T extends { archived_at: string | null }>(rows: T[], url?: URL) =>
    url?.searchParams.get("include_archived") === "true"
      ? rows
      : rows.filter((r) => !r.archived_at);

  function boardIn(p: Principal, id: string | undefined): Board | null {
    return stateOf(p.company).boards.find((b) => b.id === id) ?? null;
  }
  function taskIn(p: Principal, id: string | undefined): StoredTask | null {
    return stateOf(p.company).tasks.find((t) => t.id === id && !t.deleted) ?? null;
  }
  function spaceIn(p: Principal, id: string | undefined): Space | null {
    return stateOf(p.company).spaces.find((s) => s.id === id) ?? null;
  }
  const fieldsOf = (p: Principal, boardId: string) =>
    stateOf(p.company)
      .fields.filter((f) => f.board_id === boardId)
      .sort((a, b) => a.position - b.position);
  const viewsOf = (p: Principal, boardId: string) =>
    stateOf(p.company)
      .views.filter((v) => v.board_id === boardId)
      .sort((a, b) => a.position - b.position);
  const seatList = (p: Principal) => seatRows(p.company, deps.desOf(p.company.id));
  const seatIn = (p: Principal, id: string) => seatList(p).find((s) => s.id === id) ?? null;

  function blocked(p: Principal, t: StoredTask): boolean {
    return t.dependencies.some((d) => {
      if (d.kind !== "hard") return false;
      const on = taskIn(p, d.depends_on_task_id);
      if (!on) return false;
      const board = boardIn(p, on.board_id);
      return !isDoneStatus(board?.status_set ?? [], on.status);
    });
  }

  /** TASK-LOCK (domain/boards/task-lock.ts): the run working on the task now, or null. */
  function lockOf(
    p: Principal,
    taskId: string,
    except: (membershipId: string) => boolean = () => false,
  ): NonNullable<TaskCard["agent_lock"]> | null {
    const now = deps.now().toISOString();
    const run = (deps.runsOf?.(p.company.id) ?? []).find(
      (r) =>
        r.task_id === taskId &&
        r.state === "running" &&
        r.id !== undefined &&
        (r.lease_until == null || r.lease_until > now) &&
        !except(r.membership_id),
    );
    if (!run?.id) return null;
    const de = deps.desOf(p.company.id).find((d) => d.membership_id === run.membership_id);
    return {
      run_id: run.id,
      membership_id: run.membership_id,
      name: de?.display_name ?? "An agent",
      since: run.started_at ?? run.created_at,
      can_stop: canStopAgents(p, taskId),
    };
  }

  /** The live runs on a task (every agent's), for "Stop agent and move". */
  function liveRunsOn(p: Principal, taskId: string): LiveRunLike[] {
    const now = deps.now().toISOString();
    return (deps.runsOf?.(p.company.id) ?? []).filter(
      (r) =>
        r.task_id === taskId &&
        r.state === "running" &&
        r.id !== undefined &&
        (r.lease_until == null || r.lease_until > now),
    );
  }

  /**
   * TASK-LOCK (task-lock.ts `canStopAgents`): admins and operators, the person who created the
   * task, or a holder of one of its assigned seats.
   */
  function canStopAgents(p: Principal, taskId: string): boolean {
    const me = membershipOf(p);
    if (!me) return false;
    if (p.roles.some((r) => r === "owner" || r === "admin" || r === "agency_operator")) return true;
    const t = taskIn(p, taskId);
    if (!t) return false;
    if (t.actor_kind === "person" && t.actor_membership_id === me) return true;
    const held = new Set(
      deps
        .desOf(p.company.id)
        .filter((d) => d.supervisor.membership_id === me)
        .map((d) => d.supervisor.seat_id),
    );
    return t.assignee_seat_ids.some((s) => held.has(s));
  }

  /** TASK-LOCK: 409 `task_locked` unless the caller is the agent working on it. */
  function lockRefusal(p: Principal, t: StoredTask): Response | null {
    // Any live run of another agent locks it (the caller's own runs never do).
    const lock = lockOf(p, t.id, (id) => p.me.memberships.some((m) => m.membership_id === id));
    if (!lock) return null;
    return problem(
      409,
      "task_locked",
      "An agent is working on this task",
      `locked: ${lock.name} is working on this`,
    );
  }

  function card(p: Principal, t: StoredTask): TaskCard {
    const { checklist_items, checklists: _c, dependencies: _d, deleted: _x, ...row } = t;
    return {
      agent_lock: lockOf(p, t.id),
      ...row,
      assignee_seat_ids: [...t.assignee_seat_ids],
      fields: { ...t.fields },
      checklist: {
        done: checklist_items.filter((c) => c.done_at).length,
        total: checklist_items.length,
      },
      blocked: blocked(p, t),
      depends_on_task_ids: t.dependencies.map((d) => d.depends_on_task_id).sort(),
    };
  }

  /** WORK-OS `boards.summary`: one row per live board, counted over its tasks like the API. */
  function summaryOf(p: Principal, b: Board) {
    const now = deps.now().toISOString();
    const tasks = stateOf(p.company).tasks.filter((t) => t.board_id === b.id && !t.deleted);
    const open = tasks.filter((t) => t.completed_at === null);
    const seatCounts = new Map<string, number>();
    for (const t of open)
      for (const s of t.assignee_seat_ids) seatCounts.set(s, (seatCounts.get(s) ?? 0) + 1);
    const seats = [...seatCounts.entries()]
      .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
      .slice(0, 8)
      .map(([s]) => s);
    const dues = open.flatMap((t) => (t.due_at && t.due_at >= now ? [t.due_at] : [])).sort();
    // The twin's spaces are not team-bound (`team_id` null): the lead is the board's own.
    const lead = b.lead_seat_id;
    return {
      board_id: b.id,
      space_id: b.space_id,
      name: b.name,
      home_channel_id: b.home_channel_id,
      archived_at: b.archived_at,
      lead_seat_id: lead,
      color: b.color,
      position: b.position,
      assignee_seat_ids: seats,
      total: tasks.length,
      done: tasks.length - open.length,
      open: open.length,
      overdue: open.filter((t) => t.due_at !== null && t.due_at < now).length,
      blocked: open.filter((t) => blocked(p, t)).length,
      next_due_at: dues[0] ?? null,
      last_activity_at:
        tasks
          .map((t) => t.updated_at)
          .sort()
          .at(-1) ?? null,
    };
  }

  // Checklists (DOCS-KB decision 8): named, several per task, ordered by position then id; an
  // item added without a checklist goes to the first one, made as "Checklist" on demand.
  type Checklist = StoredTask["checklists"][number];
  type ChecklistItem = StoredTask["checklist_items"][number];
  const byPos = <T extends { position: number; id: string }>(a: T, b: T) =>
    a.position - b.position || a.id.localeCompare(b.id);
  const listsOf = (t: StoredTask) => [...t.checklists].sort(byPos);
  const itemsOf = (t: StoredTask, listId: string) =>
    t.checklist_items.filter((i) => i.checklist_id === listId).sort(byPos);
  const withItems = (t: StoredTask, l: Checklist) => ({
    ...l,
    items: itemsOf(t, l.id).map((i) => ({ ...i })),
  });
  const nextItemPos = (t: StoredTask, listId: string) =>
    itemsOf(t, listId).reduce((n, i) => Math.max(n, i.position), -1) + 1;

  function newChecklist(p: Principal, t: StoredTask, title: string, position?: number) {
    const at = stamp();
    const l: Checklist = {
      company_id: t.company_id,
      id: ids.next(0x5610),
      task_id: t.id,
      title,
      position: position ?? t.checklists.reduce((n, c) => Math.max(n, c.position), -1) + 1,
      ...rowActor(p),
      created_at: at,
      updated_at: at,
    };
    t.checklists.push(l);
    return l;
  }

  function newItem(
    p: Principal,
    t: StoredTask,
    listId: string,
    text: string,
    extra: {
      position?: number | undefined;
      assignee_seat_id?: string | null | undefined;
      due_at?: string | null | undefined;
    } = {},
  ): ChecklistItem {
    const at = stamp();
    const item: ChecklistItem = {
      company_id: t.company_id,
      id: ids.next(0x5600),
      task_id: t.id,
      checklist_id: listId,
      text,
      done_at: null,
      done_by_seat_id: null,
      assignee_seat_id: extra.assignee_seat_id ?? null,
      due_at: extra.due_at ?? null,
      position: extra.position ?? nextItemPos(t, listId),
      ...rowActor(p),
      created_at: at,
      updated_at: at,
    };
    t.checklist_items.push(item);
    return item;
  }

  function checklistChanged(
    p: Principal,
    t: StoredTask,
    checklistId: string | null,
    itemId: string | null,
    op: string,
    done: boolean,
  ) {
    record(
      p.company,
      p,
      "task.checklist_changed",
      t.id,
      { board_id: t.board_id, checklist_id: checklistId, item_id: itemId, op, done },
      stamp(),
      2,
    );
  }

  /** An item's assignee must be an open seat of the company (else a 422). */
  function badAssignee(p: Principal, v: unknown): Response | null {
    if (v === undefined || v === null) return null;
    const seat = seatIn(p, String(v));
    return !seat || seat.status === "closed"
      ? validation(`assignee_seat_id: no open seat ${String(v)}`)
      : null;
  }

  function detail(p: Principal, t: StoredTask): TaskDetail {
    return {
      ...card(p, t),
      checklists: listsOf(t).map((l) => ({ ...l })),
      // Flat, in the checklists' order, then each one's items' order.
      checklist_items: listsOf(t).flatMap((l) => itemsOf(t, l.id).map((i) => ({ ...i }))),
      dependencies: [...t.dependencies],
      field_values: Object.entries(t.fields).map(([field_def_id, value]) => ({
        company_id: t.company_id,
        task_id: t.id,
        field_def_id,
        value,
        actor_kind: t.actor_kind,
        actor_membership_id: t.actor_membership_id,
        created_at: t.created_at,
        updated_at: t.updated_at,
      })),
      assignees: t.assignee_seat_ids.map((seat_id) => ({
        company_id: t.company_id,
        task_id: t.id,
        seat_id,
        actor_kind: t.actor_kind,
        actor_membership_id: t.actor_membership_id,
        created_at: t.created_at,
      })),
    };
  }

  function contextOf(p: Principal, board: Board): ViewContext {
    const titles = new Map(seatList(p).map((s) => [s.id, s.title]));
    return {
      statuses: board.status_set,
      fields: fieldsOf(p, board.id),
      seatLabel: (id) => titles.get(id) ?? "Unknown seat",
    };
  }

  /** Matches If-Match (`"3"` or `3`) against the row version: null when it holds. */
  function precondition(headers: Headers, version: number): Response | null {
    const raw = headers.get("if-match");
    if (!raw) return problem(428, "precondition_required", "If-Match required");
    if (raw.replaceAll('"', "") !== String(version))
      return problem(
        409,
        "conflict",
        "Conflict",
        `The row is at version ${version}; reload and retry.`,
      );
    return null;
  }

  // Task writes (shared by the single routes and bulk) -----------------------------------------

  /** Null when the move is allowed, else the problem: a hard dependency still open blocks
   * moving into a started or done status (06). */
  function setStatus(p: Principal, t: StoredTask, status: string): Response | null {
    const board = boardIn(p, t.board_id);
    const def = board?.status_set.find((s) => s.key === status);
    if (!board || !def)
      return problem(
        422,
        "status_invalid",
        "Not a status of the task's board",
        `"${status}" is not a status of this board.`,
      );
    if (t.status === status) return null;
    if ((def.done || def.started) && blocked(p, t)) {
      const open = t.dependencies
        .filter((d) => d.kind === "hard")
        .map((d) => taskIn(p, d.depends_on_task_id))
        .filter(
          (x): x is StoredTask =>
            x !== null && !isDoneStatus(boardIn(p, x.board_id)?.status_set ?? [], x.status),
        )
        .map((x) => `#${x.number} ${x.title}`);
      return problem(
        409,
        "dependency_blocked",
        "An open hard dependency blocks this status",
        `Waiting on ${open.join(", ")}.`,
      );
    }
    const from = t.status;
    t.status = status;
    t.completed_at = def.done ? (t.completed_at ?? stamp()) : null;
    touch(p, t);
    record(p.company, p, "task.status_changed", t.id, {
      board_id: t.board_id,
      from,
      to: status,
      completed: def.done,
    });
    return null;
  }

  /** Takes a seat off a task (`task.unassigned`); nothing when it is not on it. */
  function unassign(p: Principal, t: StoredTask, seatId: string): void {
    if (!t.assignee_seat_ids.includes(seatId)) return;
    t.assignee_seat_ids = t.assignee_seat_ids.filter((x) => x !== seatId);
    touch(p, t);
    record(p.company, p, "task.unassigned", t.id, { board_id: t.board_id, seat_id: seatId });
  }

  function assign(p: Principal, t: StoredTask, seatIds: string[]): Response | null {
    for (const id of seatIds) {
      const seat = seatIn(p, id);
      if (!seat) return validation(`seat_ids: ${id} is not a seat of this company`);
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat is closed");
    }
    for (const id of seatIds) {
      if (t.assignee_seat_ids.includes(id)) continue;
      t.assignee_seat_ids.push(id);
      record(p.company, p, "task.assigned", t.id, { board_id: t.board_id, seat_id: id });
    }
    touch(p, t);
    return null;
  }

  function move(p: Principal, t: StoredTask, boardId: string, status?: string): Response | null {
    const to = boardIn(p, boardId);
    if (!to || to.archived_at) return validation("board_id: no such board");
    const target =
      status ?? (to.status_set.some((s) => s.key === t.status) ? t.status : to.status_set[0]?.key);
    if (!target || !to.status_set.some((s) => s.key === target))
      return problem(422, "status_invalid", "Not a status of the task's board");
    const from = t.board_id;
    t.board_id = to.id;
    t.status = target;
    // Values of the old board's fields do not travel.
    const keep = new Set(fieldsOf(p, to.id).map((f) => f.id));
    t.fields = Object.fromEntries(Object.entries(t.fields).filter(([k]) => keep.has(k)));
    touch(p, t);
    record(p.company, p, "task.moved", t.id, {
      from_board_id: from,
      to_board_id: to.id,
      status: target,
    });
    return null;
  }

  function setField(p: Principal, t: StoredTask, fieldId: string, value: unknown): Response | null {
    const f = fieldsOf(p, t.board_id).find((x) => x.id === fieldId);
    if (!f) return notFound();
    if (f.type === "formula" || f.type === "rollup")
      return validation(`${f.name} is computed and read-only`);
    if (value !== null) {
      const choices = f.options.choices?.map((c) => c.key) ?? [];
      const bad =
        (f.type === "number" || f.type === "currency") && typeof value !== "number"
          ? "a number"
          : f.type === "select" && !(typeof value === "string" && choices.includes(value))
            ? "one of its choices"
            : f.type === "multi_select" &&
                !(Array.isArray(value) && value.every((v) => choices.includes(v)))
              ? "a list of its choices"
              : f.type === "checkbox" && typeof value !== "boolean"
                ? "true or false"
                : (f.type === "text" || f.type === "url") && typeof value !== "string"
                  ? "text"
                  : null;
      if (bad) return validation(`value: ${f.name} takes ${bad}`);
    }
    const from = t.fields[fieldId] ?? null;
    if (value === null) delete t.fields[fieldId];
    else t.fields[fieldId] = value;
    touch(p, t);
    record(p.company, p, "task.field_changed", t.id, {
      board_id: t.board_id,
      field_def_id: fieldId,
      from,
      to: value,
    });
    return null;
  }

  function remove(p: Principal, t: StoredTask) {
    t.deleted = true;
    for (const x of stateOf(p.company).tasks) {
      if (x.parent_task_id === t.id) x.parent_task_id = null;
      x.dependencies = x.dependencies.filter((d) => d.depends_on_task_id !== t.id);
    }
    record(p.company, p, "task.deleted", t.id, {
      board_id: t.board_id,
      number: t.number,
      title: t.title,
    });
  }

  function touch(p: Principal, t: StoredTask) {
    t.version += 1;
    t.updated_at = stamp();
    Object.assign(t, rowActor(p));
  }

  // Boards and views ---------------------------------------------------------------------------

  function newView(
    p: Principal,
    boardId: string,
    b: Record<string, unknown>,
    position: number,
  ): View {
    const at = stamp();
    return {
      company_id: p.company.id,
      id: ids.next(0x5300),
      board_id: boardId,
      kind: b.kind as View["kind"],
      name: String(b.name).trim(),
      filter: (b.filter as View["filter"]) ?? {},
      sort: (b.sort as View["sort"]) ?? [],
      group_by: (b.group_by as string | null | undefined) ?? null,
      visible_fields: (b.visible_fields as string[] | undefined) ?? [],
      date_field: (b.date_field as string | null | undefined) ?? null,
      card_fields: (b.card_fields as string[] | null | undefined) ?? null,
      is_default: Boolean(b.is_default),
      position,
      ...rowActor(p),
      created_at: at,
      updated_at: at,
    };
  }

  function viewProblem(p: Principal, boardId: string, b: Record<string, unknown>): Response | null {
    if (
      b.kind !== undefined &&
      !["board", "list", "table", "calendar", "timeline", "gallery"].includes(String(b.kind))
    )
      return validation("kind: not a view kind");
    if (b.name !== undefined && !String(b.name).trim()) return validation("name must not be empty");
    if (
      b.group_by !== undefined &&
      !validGroupBy((b.group_by as string | null) ?? null, { fields: fieldsOf(p, boardId) })
    )
      return validation("group_by: not a status, priority, assignee or select field of this board");
    if (b.sort !== undefined && (!Array.isArray(b.sort) || b.sort.length > 5))
      return validation("sort: at most 5 keys");
    return null;
  }

  function createBoard(
    p: Principal,
    spaceId: string,
    name: string,
    statuses: StatusDef[],
    template: string,
    extra: { color?: Board["color"] | undefined; lead_seat_id?: string | null | undefined } = {},
  ): Board {
    const s = stateOf(p.company);
    const at = stamp();
    const id = ids.next(0x5200);
    const home = {
      company_id: p.company.id,
      id: ids.next(0x5b00),
      space_id: spaceId,
      kind: "board_home" as const,
      name: `${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-home`,
      description: null,
      board_id: id,
      team_id: null,
      is_private: false,
      is_agent_room: false,
      is_default: false,
      taken_over_by_membership_id: null,
      taken_over_at: null,
      dm_key: null,
      archived_at: null,
      ...rowActor(p),
      created_at: at,
      updated_at: at,
      unread_count: 0,
      last_message_at: null,
      ...SUBSCRIBED,
    };
    s.channels.push(home);
    const first = newView(
      p,
      id,
      {
        kind: template === "blank" ? "list" : "board",
        name: template === "blank" ? "List" : "Board",
        group_by: "status",
        is_default: true,
      },
      0,
    );
    const second = newView(
      p,
      id,
      {
        kind: template === "blank" ? "board" : "list",
        name: template === "blank" ? "Board" : "List",
        group_by: "status",
      },
      1,
    );
    s.views.push(first, second);
    const board: Board = {
      company_id: p.company.id,
      id,
      space_id: spaceId,
      name,
      home_channel_id: home.id,
      status_set: statuses,
      settings: { live_cards: true, default_view_id: first.id },
      digest_interval: "daily",
      // BOARD-ORDER, as the API's default: the creation instant, so it lands last.
      position: Date.parse(at),
      color: extra.color ?? null,
      lead_seat_id: extra.lead_seat_id ?? null,
      page_id: null,
      archived_at: null,
      version: 1,
      ...rowActor(p),
      created_at: at,
      updated_at: at,
    };
    s.boards.push(board);
    return board;
  }

  function createTask(p: Principal, b: Record<string, unknown>): { task: StoredTask } | Response {
    const s = stateOf(p.company);
    const board = boardIn(p, b.board_id as string | undefined);
    if (!board || board.archived_at) return validation("board_id: no such board");
    const title = typeof b.title === "string" ? b.title.trim() : "";
    if (!title || title.length > 500) return validation("title must be 1–500 characters");
    const status = (b.status as string | undefined) ?? board.status_set[0]?.key ?? "todo";
    if (!board.status_set.some((x) => x.key === status))
      return problem(422, "status_invalid", "Not a status of the task's board");
    const priority = b.priority === undefined ? 0 : Number(b.priority);
    if (!Number.isInteger(priority) || priority < 0 || priority > 4)
      return validation("priority must be 0–4");
    const parent = (b.parent_task_id as string | null | undefined) ?? null;
    if (parent && !taskIn(p, parent)) return validation("parent_task_id: no such task");
    const seatIds = (b.assignee_seat_ids as string[] | undefined) ?? [];
    for (const id of seatIds)
      if (!seatIn(p, id)) return validation(`assignee_seat_ids: ${id} is not a seat`);
    const at = stamp();
    const id = ids.next(0x5500);
    const def = board.status_set.find((x) => x.key === status);
    const number = s.tasks.reduce((n, t) => Math.max(n, t.number), 0) + 1;
    const task: StoredTask = {
      tag_ids: [],
      company_id: p.company.id,
      id,
      board_id: board.id,
      number,
      title,
      status,
      priority,
      parent_task_id: parent,
      page_id: null,
      thread_id: null,
      due_at: (b.due_at as string | null | undefined) ?? null,
      start_at: (b.start_at as string | null | undefined) ?? null,
      estimate_minutes: (b.estimate_minutes as number | null | undefined) ?? null,
      created_by_seat_id: null,
      completed_at: def?.done ? at : null,
      origin: "manual",
      origin_ref: null,
      client_id: (b.client_id as string | undefined) ?? null,
      restricted: false,
      version: 1,
      ...rowActor(p),
      created_at: at,
      updated_at: at,
      assignee_seat_ids: [...new Set(seatIds)],
      fields: {},
      checklists: [],
      checklist_items: [],
      dependencies: [],
    };
    // `checklist` fills the task's first checklist, "Checklist" (no checklist event).
    const firstItems = (b.checklist as string[] | undefined) ?? [];
    if (firstItems.length) {
      const l = newChecklist(p, task, "Checklist", 0);
      for (const [i, text] of firstItems.entries()) newItem(p, task, l.id, text, { position: i });
    }
    for (const [fid, value] of Object.entries(
      (b.fields as Record<string, unknown> | undefined) ?? {},
    )) {
      if (!fieldsOf(p, board.id).some((f) => f.id === fid))
        return validation(`fields: ${fid} is not a field of this board`);
      task.fields[fid] = value;
    }
    s.tasks.push(task);
    record(p.company, p, "task.created", id, {
      board_id: board.id,
      number,
      title,
      status,
      priority,
      parent_task_id: parent,
      origin: "manual",
      origin_ref: null,
      assignee_seat_ids: task.assignee_seat_ids,
      due_at: task.due_at,
    });
    // The creator follows (06 §Following).
    const m = membershipOf(p);
    if (m) follow(p, m, "task", id, "all");
    return { task };
  }

  function follow(
    p: Principal,
    membership: string,
    kind: Follow["subject_kind"],
    id: string,
    level: Follow["level"],
  ): Follow {
    const s = stateOf(p.company);
    const existing = s.follows.find(
      (f) => f.membership_id === membership && f.subject_kind === kind && f.subject_id === id,
    );
    const at = stamp();
    if (existing) {
      existing.level = level;
      existing.updated_at = at;
      return existing;
    }
    const row: Follow = {
      company_id: p.company.id,
      membership_id: membership,
      subject_kind: kind,
      subject_id: id,
      level,
      ...rowActor(p),
      created_at: at,
      updated_at: at,
    };
    s.follows.push(row);
    return row;
  }

  // The chat twin's view of this one (fake.ts `twinRoutes`) ----------------------------------

  const companyById = (id: string) => deps.companies.find((c) => c.id === id) ?? null;
  deps.registerBridge?.({
    spacesOf: (companyId) => {
      const c = companyById(companyId);
      return c ? stateOf(c).spaces : null;
    },
    tasksOf: (companyId) => {
      const c = companyById(companyId);
      return c ? stateOf(c).tasks.filter((t) => !t.deleted) : null;
    },
    stateOf: (companyId) => {
      const c = companyById(companyId);
      return c ? stateOf(c) : null;
    },
    sidebarOf: (companyId, spaceId) => {
      const c = companyById(companyId);
      if (!c) return null;
      const s = stateOf(c);
      return {
        boards: s.boards.filter((b) => b.space_id === spaceId && !b.archived_at),
        channels: s.channels
          .filter((x) => x.space_id === spaceId && !x.archived_at)
          .map((x) => ({ ...SUBSCRIBED, ...x })),
      };
    },
    boardOf: (companyId, boardId) => {
      const c = companyById(companyId);
      return (c && stateOf(c).boards.find((b) => b.id === boardId && !b.archived_at)) || null;
    },
    taskOf: (companyId, taskId) => {
      const c = companyById(companyId);
      return (c && stateOf(c).tasks.find((t) => t.id === taskId && !t.deleted)) || null;
    },
    addThread: (companyId, threadId, subjectId) => {
      const c = companyById(companyId);
      if (c) stateOf(c).threads.set(threadId, subjectId);
    },
    recordTaskEvent: (companyId, taskId, type, payload, actor) => {
      const c = companyById(companyId);
      if (!c) return;
      stateOf(c).events.push({
        id: ids.next(0x5c00),
        company_id: c.id,
        seq: ++eventSeq,
        type,
        version: 1,
        actor,
        subject: { kind: "task", id: taskId },
        payload,
        correlation_id: null,
        causation_id: null,
        occurred_at: stamp(),
      });
    },
    messageTask: (companyId, messageId) => {
      const c = companyById(companyId);
      if (!c) return null;
      const s = stateOf(c);
      for (const [threadId, list] of s.messages)
        if (list.some((m) => m.id === messageId)) return s.threads.get(threadId) ?? null;
      return null;
    },
  });

  // Template application (fake-org.ts runs `templates.apply` and calls these) ----------------

  deps.registerTemplateHandlers?.({
    space(principal, { space: sp, teamId }) {
      const s = stateOf(principal.company);
      const at = stamp();
      const space: Space = {
        company_id: principal.company.id,
        id: ids.next(0x5100),
        name: sp.name,
        kind: sp.kind,
        team_id: teamId,
        owner_membership_id: null,
        icon: null,
        description: null,
        is_private: false,
        // MEMBER-UX, as `spaces.create`: a shared space lets members edit.
        settings: teamId ? {} : { member_access: "editor" },
        position: Date.parse(at),
        archived_at: null,
        ...rowActor(principal),
        created_at: at,
        updated_at: at,
      };
      s.spaces.push(space);
      const channelIds: string[] = [];
      for (const ch of sp.channels ?? []) {
        const id = ids.next(0x5b00);
        s.channels.push({
          company_id: principal.company.id,
          id,
          space_id: space.id,
          kind: "space",
          name: ch.name.toLowerCase().replace(/[^a-z0-9-]+/g, "-"),
          description: null,
          board_id: null,
          team_id: null,
          is_private: Boolean(ch.is_private),
          is_agent_room: false,
          is_default: false,
          taken_over_by_membership_id: null,
          taken_over_at: null,
          dm_key: null,
          archived_at: null,
          ...rowActor(principal),
          created_at: at,
          updated_at: at,
          unread_count: 0,
          last_message_at: null,
          ...SUBSCRIBED,
        });
        channelIds.push(id);
      }
      return { spaceId: space.id, channelIds };
    },
    board(principal, { templateId, spaceId, board: bd }) {
      // Every open status after the first counts as started (hard dependencies block it).
      const statuses: StatusDef[] = bd.statuses?.length
        ? bd.statuses.map((x, i) => (!x.done && i > 0 ? { ...x, started: true } : { ...x }))
        : BASIC_STATUSES;
      const board = createBoard(principal, spaceId, bd.name, statuses, "kanban");
      const channelIds = board.home_channel_id ? [board.home_channel_id] : [];
      const taskIds: string[] = [];
      for (const tt of bd.tasks ?? []) {
        const due =
          tt.due_in_days === undefined
            ? null
            : new Date(deps.now().getTime() + tt.due_in_days * 86_400_000).toISOString();
        const made = createTask(principal, {
          board_id: board.id,
          title: tt.title,
          priority: tt.priority ?? 0,
          due_at: due,
          assignee_seat_ids: tt.assignee_seat_ids ?? [],
          checklist: tt.checklist ?? [],
        });
        if (made instanceof Response) return made;
        made.task.origin = "template";
        made.task.origin_ref = { template_id: templateId };
        taskIds.push(made.task.id);
      }
      return { boardId: board.id, channelIds, taskIds };
    },
  });

  // Handlers -----------------------------------------------------------------------------------

  const isUuid = (v: unknown): v is string => typeof v === "string" && UUID.test(v);

  // Parity P1 undo records (services/api domain/undo): the four reference writes keep what they
  // changed; `undo.apply` puts it back for the same member within 15 minutes, refusing when the
  // written value is gone since, and answers a second undo as a replay.
  type FakeUndo = {
    id: string;
    companyId: string;
    membership: string | null;
    taskId: string;
    kind: "task.status" | "task.fields" | "task.field" | "task.move";
    from: unknown;
    to: unknown;
    fieldId?: string;
    at: number;
    usedAt: string | null;
  };
  const undos = new Map<string, FakeUndo>();
  const PATCH_KEYS = [
    "title",
    "priority",
    "parent_task_id",
    "due_at",
    "start_at",
    "estimate_minutes",
  ] as const;
  type PatchKey = (typeof PATCH_KEYS)[number];
  const patchOf = (t: StoredTask) =>
    Object.fromEntries(PATCH_KEYS.map((k) => [k, t[k]])) as Record<PatchKey, unknown>;
  const placeOf = (t: StoredTask) => ({ board_id: t.board_id, status: t.status });
  const sameJson = (a: unknown, b: unknown) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  function undoOf(
    p: Principal,
    t: StoredTask,
    kind: FakeUndo["kind"],
    from: unknown,
    to: unknown,
    fieldId?: string,
  ): string {
    const id = ids.next(0x5d00);
    undos.set(id, {
      id,
      companyId: p.company.id,
      membership: membershipOf(p),
      taskId: t.id,
      kind,
      from,
      to,
      ...(fieldId ? { fieldId } : {}),
      at: deps.now().getTime(),
      usedAt: null,
    });
    return id;
  }
  /** What the undo's write set, as the task reads now (compared with `to`). */
  function currentOf(t: StoredTask, u: FakeUndo): unknown {
    if (u.kind === "task.status") return t.status;
    if (u.kind === "task.move") return placeOf(t);
    if (u.kind === "task.field") return t.fields[u.fieldId ?? ""] ?? null;
    const now = patchOf(t);
    return Object.fromEntries(Object.keys(u.to as object).map((k) => [k, now[k as PatchKey]]));
  }
  const WHAT: Record<FakeUndo["kind"], string> = {
    "task.status": "status",
    "task.fields": "task",
    "task.field": "field",
    "task.move": "board",
  };

  const handlers: Partial<Record<RouteId, Handler>> = {
    // Spaces (M2-02's routes; the twin serves the ones Work mode reads and writes).
    "spaces.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json(page([], url));
      const kind = url.searchParams.get("kind");
      const me = membershipOf(principal);
      // A personal space (M4-03: the crew's `#brief` lives in one) is its owner's only.
      const rows = live(stateOf(principal.company).spaces, url).filter(
        (s) =>
          (!kind || s.kind === kind) && (s.kind !== "personal" || s.owner_membership_id === me),
      );
      return json(page(rows, url));
    },
    "spaces.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const space = spaceIn(principal, params.id);
      return space ? json(space) : notFound();
    },
    "spaces.create": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const name = typeof b.name === "string" ? b.name.trim() : "";
      if (!name) return validation("name must not be empty");
      if (!["team", "project", "client"].includes(String(b.kind)))
        return validation("kind: team, project or client");
      const at = stamp();
      const space: Space = {
        company_id: principal.company.id,
        id: ids.next(0x5100),
        name,
        kind: b.kind as Space["kind"],
        team_id: (b.team_id as string | null | undefined) ?? null,
        owner_membership_id: null,
        icon: (b.icon as string | null | undefined) ?? null,
        description: (b.description as string | null | undefined) ?? null,
        is_private: Boolean(b.is_private),
        // MEMBER-UX, as the API: a shared space (no team, not private) lets members edit.
        settings:
          !b.team_id &&
          !b.is_private &&
          (b.settings as Space["settings"] | undefined)?.member_access === undefined
            ? { ...(b.settings as Space["settings"] | undefined), member_access: "editor" }
            : ((b.settings as Space["settings"] | undefined) ?? {}),
        position: Date.parse(at),
        archived_at: null,
        ...rowActor(principal),
        created_at: at,
        updated_at: at,
      };
      const s = stateOf(principal.company);
      s.spaces.push(space);
      s.channels.push({
        company_id: principal.company.id,
        id: ids.next(0x5b00),
        space_id: space.id,
        kind: "space",
        name: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        description: null,
        board_id: null,
        team_id: null,
        is_private: false,
        is_agent_room: false,
        // The space's own channel: its default (the real API joins everyone who sees the space).
        is_default: true,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        dm_key: null,
        archived_at: null,
        ...rowActor(principal),
        created_at: at,
        updated_at: at,
        unread_count: 0,
        last_message_at: null,
        ...SUBSCRIBED,
      });
      return json(space, 201);
    },
    "spaces.update": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const space = spaceIn(principal, params.id);
      if (!space) return notFound();
      // The space's owners (company#admin here): a member cannot change who edits (MEMBER-UX).
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.name !== undefined) {
        const name = String(b.name).trim();
        if (!name) return validation("name must not be empty");
        space.name = name;
      }
      if (b.description !== undefined) space.description = (b.description as string | null) ?? null;
      if (b.icon !== undefined) space.icon = (b.icon as string | null) ?? null;
      if (b.is_private !== undefined) space.is_private = Boolean(b.is_private);
      if (b.settings !== undefined) {
        // BOARD-DEFAULT-VIEW: `overview` or a board id (null: its first board).
        const opens = (b.settings as { opens_on?: unknown }).opens_on;
        if (
          opens !== undefined &&
          opens !== null &&
          opens !== "overview" &&
          !(typeof opens === "string" && /^[0-9a-f-]{36}$/i.test(opens))
        )
          return validation("settings.opens_on: `overview` or a board id");
        space.settings = b.settings as Space["settings"];
      }
      space.updated_at = stamp();
      return json(space);
    },
    "spaces.archive": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const space = spaceIn(principal, params.id);
      if (!space) return notFound();
      if (space.archived_at)
        return problem(409, "conflict", "Conflict", "The space is already archived.");
      const at = stamp();
      space.archived_at = at;
      const s = stateOf(principal.company);
      for (const b of s.boards) if (b.space_id === space.id && !b.archived_at) b.archived_at = at;
      for (const c of s.channels) if (c.space_id === space.id && !c.archived_at) c.archived_at = at;
      return json(space);
    },
    // ACTIONS: back with exactly what the archive took (archived at the same instant).
    "spaces.unarchive": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const space = spaceIn(principal, params.id);
      if (!space) return notFound();
      const at = space.archived_at;
      if (!at) return problem(409, "conflict", "Conflict", "The space is not archived.");
      const s = stateOf(principal.company);
      for (const b of s.boards)
        if (b.space_id === space.id && b.archived_at === at) b.archived_at = null;
      for (const c of s.channels)
        if (c.space_id === space.id && c.archived_at === at) c.archived_at = null;
      space.archived_at = null;
      space.updated_at = stamp();
      return json(space);
    },
    "spaces.sidebar": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const space = spaceIn(principal, params.id);
      if (!space) return notFound();
      const s = stateOf(principal.company);
      const channels = s.channels
        .filter((c) => c.space_id === space.id && !c.archived_at)
        .map((c) => ({ ...SUBSCRIBED, ...c }));
      return json({
        space,
        channels,
        boards: s.boards.filter((b) => b.space_id === space.id && !b.archived_at),
        pages: [],
        unread_total: channels.reduce((n, c) => n + c.unread_count, 0),
      });
    },

    // Boards
    "boards.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json(page([], url));
      const space = url.searchParams.get("space_id");
      const rows = live(stateOf(principal.company).boards, url).filter(
        (b) => !space || b.space_id === space,
      );
      return json(page(rows, url));
    },
    "boards.summary": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json({ data: [] });
      const space = url.searchParams.get("space_id");
      const rows = live(stateOf(principal.company).boards, url)
        .filter((b) => !space || b.space_id === space)
        .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
        .map((b) => summaryOf(principal, b));
      return json({ data: rows });
    },
    "boards.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      return board ? json(board) : notFound();
    },
    "boards.create": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const space = spaceIn(principal, b.space_id as string | undefined);
      if (!space || space.archived_at) return validation("space_id: no such space");
      const name = typeof b.name === "string" ? b.name.trim() : "";
      if (!name) return validation("name must not be empty");
      const template = (b.template as string | undefined) ?? "blank";
      if (!(BOARD_TEMPLATE_KEYS as readonly string[]).includes(template))
        return validation("template: not a board template");
      // AWARENESS-MVP: the Ideas template's own statuses win over the space's default set.
      const fixed = BOARD_TEMPLATES[template as BoardTemplateKey].fixedStatuses
        ? [...BOARD_TEMPLATES[template as BoardTemplateKey].statusSet]
        : undefined;
      const statuses =
        (b.status_set as StatusDef[] | undefined) ??
        fixed ??
        space.settings.default_status_set ??
        BASIC_STATUSES;
      if (
        !statuses.length ||
        !statuses.some((x) => x.done) ||
        statuses.some((x) => !KEY.test(x.key))
      )
        return validation("status_set: keys like `in_review` and at least one done status");
      const color = b.color as Board["color"] | undefined;
      if (color !== undefined && color !== null && !BOARD_COLORS.includes(color))
        return validation("color: one of the six tints");
      if (b.lead_seat_id !== undefined && b.lead_seat_id !== null && !isUuid(b.lead_seat_id))
        return validation("lead_seat_id: no such seat");
      const board = createBoard(principal, space.id, name, statuses, template, {
        color,
        lead_seat_id: b.lead_seat_id as string | null | undefined,
      });
      return json(board, 201);
    },
    "boards.update": ({ principal, params, body, headers }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      const pre = precondition(headers, board.version);
      if (pre) return pre;
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.name !== undefined) {
        const name = String(b.name).trim();
        if (!name) return validation("name must not be empty");
        board.name = name;
      }
      if (b.status_set !== undefined) {
        const next = b.status_set as StatusDef[];
        if (!next.length || !next.some((x) => x.done))
          return validation("status_set: at least one done status");
        const keys = new Set(next.map((x) => x.key));
        const used = stateOf(principal.company).tasks.filter(
          (t) => t.board_id === board.id && !t.deleted && !keys.has(t.status),
        );
        if (used.length)
          return problem(
            409,
            "status_in_use",
            "A removed status still has tasks",
            `${used.length} task(s) still use a removed status.`,
          );
        board.status_set = next;
      }
      if (b.settings !== undefined) {
        // WORKFORCE-BOARDVIEW: the tab the board opens on (null clears it).
        const kind = (b.settings as { default_kind?: unknown }).default_kind;
        if (kind !== undefined && kind !== null && !OPENING_KINDS.includes(kind as never))
          return validation("settings.default_kind: one of the six view kinds");
        board.settings = { ...board.settings, ...(b.settings as Board["settings"]) };
      }
      if (b.digest_interval !== undefined)
        board.digest_interval = b.digest_interval as Board["digest_interval"];
      // BOARD-ORDER: the colour (null: the tint by name) and the lead seat.
      if (b.color !== undefined) {
        if (b.color !== null && !BOARD_COLORS.includes(b.color as NonNullable<Board["color"]>))
          return validation("color: one of the six tints");
        board.color = b.color as Board["color"];
      }
      if (b.lead_seat_id !== undefined) {
        if (b.lead_seat_id !== null && !isUuid(b.lead_seat_id))
          return validation("lead_seat_id: no such seat");
        board.lead_seat_id = b.lead_seat_id as string | null;
      }
      board.version += 1;
      board.updated_at = stamp();
      return json(board);
    },
    "boards.archive": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      if (board.archived_at)
        return problem(409, "conflict", "Conflict", "The board is already archived.");
      board.archived_at = stamp();
      board.version += 1;
      const home = stateOf(principal.company).channels.find((c) => c.id === board.home_channel_id);
      if (home) home.archived_at = board.archived_at;
      return json(board);
    },
    // ACTIONS: back with the home channel its archive took; an archived space comes first.
    "boards.unarchive": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      const at = board.archived_at;
      if (!at) return problem(409, "conflict", "Conflict", "The board is not archived.");
      if (spaceIn(principal, board.space_id)?.archived_at)
        return problem(409, "conflict", "Conflict", "The space is archived; restore it first.");
      board.archived_at = null;
      board.version += 1;
      board.updated_at = stamp();
      const home = stateOf(principal.company).channels.find((c) => c.id === board.home_channel_id);
      if (home && home.archived_at === at) home.archived_at = null;
      return json(board);
    },
    // BOARD-ORDER: as the API, a move renumbers the target space's live boards 0..n-1.
    "boards.move": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      if (board.archived_at) return problem(409, "conflict", "Conflict", "The board is archived.");
      const b = (body ?? {}) as Record<string, unknown>;
      const target = spaceIn(principal, (b.space_id as string | undefined) ?? board.space_id);
      if (!target) return validation("space_id: no such space");
      if (target.archived_at) return problem(409, "conflict", "Conflict", "The space is archived.");
      const s = stateOf(principal.company);
      const order = s.boards
        .filter((x) => x.space_id === target.id && !x.archived_at && x.id !== board.id)
        .sort(byPosition);
      const before = (b.before_board_id as string | null | undefined) ?? null;
      const at = before === null ? order.length : order.findIndex((x) => x.id === before);
      if (at < 0) return validation("before_board_id: not a board of that space");
      order.splice(at, 0, board);
      for (const [i, x] of order.entries()) x.position = i;
      if (board.space_id !== target.id) {
        board.space_id = target.id;
        const home = s.channels.find((c) => c.id === board.home_channel_id);
        if (home) {
          home.space_id = target.id;
          home.is_private = target.is_private;
        }
      }
      board.version += 1;
      board.updated_at = stamp();
      return json(board);
    },
    "spaces.move": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const space = spaceIn(principal, params.id);
      if (!space) return notFound();
      // The company's order is its admins' (the API: company#admin).
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      if (space.kind === "personal" || space.archived_at)
        return problem(409, "conflict", "Conflict", "This space has no place in the order.");
      const order = stateOf(principal.company)
        .spaces.filter((x) => x.kind !== "personal" && !x.archived_at && x.id !== space.id)
        .sort(byPosition);
      const before = ((body ?? {}) as { before_space_id?: string | null }).before_space_id ?? null;
      const at = before === null ? order.length : order.findIndex((x) => x.id === before);
      if (at < 0) return validation("before_space_id: not a live shared space");
      order.splice(at, 0, space);
      for (const [i, x] of order.entries()) x.position = i;
      space.updated_at = stamp();
      return json(space);
    },
    "boards.tasks": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      const views = viewsOf(principal, board.id);
      const viewId = url.searchParams.get("view");
      const view = viewId
        ? views.find((v) => v.id === viewId)
        : (views.find((v) => v.id === board.settings.default_view_id) ??
          views.find((v) => v.is_default) ??
          views[0]);
      if (viewId && !view) return validation("view: not a view of this board");
      const tasks = stateOf(principal.company)
        .tasks.filter((t) => t.board_id === board.id && !t.deleted)
        .map((t) => card(principal, t));
      const groups = applyView(
        tasks,
        view ?? { filter: {}, sort: [], group_by: null },
        contextOf(principal, board),
      );
      if (!view?.group_by) {
        const paged = page(groups[0]?.tasks ?? [], url);
        return json({
          view_id: view?.id ?? null,
          data: paged.data,
          groups: null,
          next_cursor: paged.next_cursor,
        });
      }
      const only = url.searchParams.get("group");
      const shown = only === null ? groups : groups.filter((g) => (g.key ?? NONE_GROUP) === only);
      return json({
        view_id: view.id,
        data: [],
        groups: shown.map((g) => {
          const paged = page(g.tasks, only === null ? new URL(url.pathname, url) : url);
          return {
            key: g.key,
            label: g.label,
            count: g.tasks.length,
            data: paged.data,
            next_cursor: paged.next_cursor,
          };
        }),
        next_cursor: null,
      });
    },

    // Views
    "views.list": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      return board ? json({ data: viewsOf(principal, board.id) }) : notFound();
    },
    "views.create": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.kind === undefined || b.name === undefined)
        return validation("kind and name are required");
      const bad = viewProblem(principal, board.id, b);
      if (bad) return bad;
      const views = viewsOf(principal, board.id);
      const view = newView(
        principal,
        board.id,
        b,
        (b.position as number | undefined) ?? views.length,
      );
      if (view.is_default) for (const v of views) v.is_default = false;
      stateOf(principal.company).views.push(view);
      if (view.is_default) board.settings = { ...board.settings, default_view_id: view.id };
      return json(view, 201);
    },
    "views.update": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      const view = board
        ? viewsOf(principal, board.id).find((v) => v.id === params.sub_id)
        : undefined;
      if (!board || !view) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if ("kind" in b) return validation("kind: a view's kind never changes; make a new view");
      const bad = viewProblem(principal, board.id, b);
      if (bad) return bad;
      if (b.name !== undefined) view.name = String(b.name).trim();
      if (b.filter !== undefined) view.filter = b.filter as View["filter"];
      if (b.sort !== undefined) view.sort = b.sort as View["sort"];
      if (b.group_by !== undefined) view.group_by = (b.group_by as string | null) ?? null;
      if (b.visible_fields !== undefined) view.visible_fields = b.visible_fields as string[];
      if (b.date_field !== undefined) view.date_field = (b.date_field as string | null) ?? null;
      if (b.card_fields !== undefined)
        view.card_fields = (b.card_fields as string[] | null) ?? null;
      if (b.position !== undefined) view.position = Number(b.position);
      if (b.is_default === true) {
        for (const v of viewsOf(principal, board.id)) v.is_default = v.id === view.id;
        board.settings = { ...board.settings, default_view_id: view.id };
      }
      view.updated_at = stamp();
      Object.assign(view, rowActor(principal));
      return json(view);
    },
    "views.delete": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      const views = board ? viewsOf(principal, board.id) : [];
      const view = views.find((v) => v.id === params.sub_id);
      if (!board || !view) return notFound();
      if (views.length === 1)
        return problem(409, "conflict", "Conflict", "A board keeps at least one view.");
      const s = stateOf(principal.company);
      s.views = s.views.filter((v) => v.id !== view.id);
      if (view.is_default || board.settings.default_view_id === view.id) {
        const next = viewsOf(principal, board.id)[0];
        if (next) {
          next.is_default = true;
          board.settings = { ...board.settings, default_view_id: next.id };
        }
      }
      return new Response(null, { status: 204 });
    },

    // Fields
    "fields.list": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      return board ? json({ data: fieldsOf(principal, board.id) }) : notFound();
    },
    "fields.create": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const name = typeof b.name === "string" ? b.name.trim() : "";
      if (!name) return validation("name must not be empty");
      if (fieldsOf(principal, board.id).some((f) => f.name.toLowerCase() === name.toLowerCase()))
        return problem(409, "conflict", "Conflict", `The board already has a field "${name}".`);
      const at = stamp();
      const field: FieldDef = {
        company_id: principal.company.id,
        id: ids.next(0x5400),
        board_id: board.id,
        name,
        type: b.type as FieldDef["type"],
        options: (b.options as FieldDef["options"] | undefined) ?? {},
        audience: (b.audience as FieldDef["audience"] | undefined) ?? "both",
        requires_approval: Boolean(b.requires_approval),
        position: (b.position as number | undefined) ?? fieldsOf(principal, board.id).length,
        ...rowActor(principal),
        created_at: at,
        updated_at: at,
      };
      stateOf(principal.company).fields.push(field);
      return json(field, 201);
    },
    "fields.update": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const field = stateOf(principal.company).fields.find(
        (f) => f.id === params.sub_id && f.board_id === params.id,
      );
      if (!field) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.name !== undefined) field.name = String(b.name).trim() || field.name;
      if (b.options !== undefined) field.options = b.options as FieldDef["options"];
      if (b.audience !== undefined) field.audience = b.audience as FieldDef["audience"];
      if (b.requires_approval !== undefined) field.requires_approval = Boolean(b.requires_approval);
      if (b.position !== undefined) field.position = Number(b.position);
      field.updated_at = stamp();
      return json(field);
    },
    "fields.delete": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const s = stateOf(principal.company);
      const field = s.fields.find((f) => f.id === params.sub_id && f.board_id === params.id);
      if (!field) return notFound();
      s.fields = s.fields.filter((f) => f.id !== field.id);
      for (const t of s.tasks) delete t.fields[field.id];
      for (const v of s.views) if (v.group_by === `field:${field.id}`) v.group_by = "status";
      return new Response(null, { status: 204 });
    },

    // Tasks
    "tasks.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json(page([], url));
      const q = url.searchParams;
      const rows = stateOf(principal.company)
        .tasks.filter(
          (t) =>
            !t.deleted &&
            (!q.get("board_id") || t.board_id === q.get("board_id")) &&
            (!q.get("assignee_seat_id") ||
              t.assignee_seat_ids.includes(q.get("assignee_seat_id") ?? "")) &&
            (!q.get("status") || t.status === q.get("status")) &&
            (!q.get("parent_task_id") || t.parent_task_id === q.get("parent_task_id")),
        )
        .sort((a, b) => a.number - b.number)
        .map((t) => card(principal, t));
      return json(page(rows, url));
    },
    "tasks.create": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const s = stateOf(principal.company);
      if (typeof b.client_id === "string") {
        const existing = taskIn(principal, s.clientIds.get(b.client_id));
        if (existing) return json(detail(principal, existing), 201);
      }
      const made = createTask(principal, b);
      if (made instanceof Response) return made;
      if (typeof b.client_id === "string") s.clientIds.set(b.client_id, made.task.id);
      return json(detail(principal, made.task), 201);
    },
    "tasks.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      return t ? json(detail(principal, t)) : notFound();
    },
    "tasks.update": ({ principal, params, body, headers }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const pre = precondition(headers, t.version);
      if (pre) return pre;
      const was = patchOf(t);
      const b = (body ?? {}) as Record<string, unknown>;
      const changes: Record<string, { from: unknown; to: unknown }> = {};
      if (b.title !== undefined) {
        const title = String(b.title).trim();
        if (!title || title.length > 500) return validation("title must be 1–500 characters");
        if (title !== t.title) changes.title = { from: t.title, to: title };
      }
      if (b.parent_task_id !== undefined) {
        const parent = b.parent_task_id as string | null;
        if (parent && (parent === t.id || !taskIn(principal, parent)))
          return validation("parent_task_id: no such task");
        if (parent !== t.parent_task_id)
          changes.parent_task_id = { from: t.parent_task_id, to: parent };
      }
      const start = b.start_at === undefined ? t.start_at : (b.start_at as string | null);
      const due = b.due_at === undefined ? t.due_at : (b.due_at as string | null);
      if (start && due && start > due)
        return validation("start_at: the start is after the due date");
      if (b.start_at !== undefined && start !== t.start_at)
        changes.start_at = { from: t.start_at, to: start };
      if (b.estimate_minutes !== undefined && b.estimate_minutes !== t.estimate_minutes)
        changes.estimate_minutes = { from: t.estimate_minutes, to: b.estimate_minutes };
      const priority = b.priority === undefined ? t.priority : Number(b.priority);
      if (!Number.isInteger(priority) || priority < 0 || priority > 4)
        return validation("priority must be 0–4");
      const dueFrom = t.due_at;
      const priorityFrom = t.priority;
      if (changes.title) t.title = String(changes.title.to);
      if (changes.parent_task_id) t.parent_task_id = changes.parent_task_id.to as string | null;
      if (changes.start_at) t.start_at = start;
      if (changes.estimate_minutes)
        t.estimate_minutes = changes.estimate_minutes.to as number | null;
      t.due_at = due;
      t.priority = priority;
      touch(principal, t);
      // `task.updated` carries only what changed; null `from`/`to` values are left out as the
      // payload's typed fields require (changesOf).
      if (Object.keys(changes).length)
        record(principal.company, principal, "task.updated", t.id, {
          board_id: t.board_id,
          changes: Object.fromEntries(
            Object.entries(changes).map(([k, v]) => [
              k,
              Object.fromEntries(
                Object.entries(v).filter(([, x]) => x !== null && x !== undefined),
              ),
            ]),
          ),
          version: t.version,
        });
      if (due !== dueFrom)
        record(principal.company, principal, "task.due_changed", t.id, {
          board_id: t.board_id,
          from: dueFrom,
          to: due,
        });
      if (priority !== priorityFrom)
        record(principal.company, principal, "task.priority_changed", t.id, {
          board_id: t.board_id,
          from: priorityFrom,
          to: priority,
        });
      const now = patchOf(t);
      const changed = PATCH_KEYS.filter((k) => !sameJson(was[k], now[k]));
      const undo_id = changed.length
        ? undoOf(
            principal,
            t,
            "task.fields",
            Object.fromEntries(changed.map((k) => [k, was[k]])),
            Object.fromEntries(changed.map((k) => [k, now[k]])),
          )
        : null;
      return json({ ...detail(principal, t), undo_id });
    },
    "tasks.delete": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      remove(principal, t);
      return new Response(null, { status: 204 });
    },
    "tasks.move": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const locked = lockRefusal(principal, t);
      if (locked) return locked;
      const b = (body ?? {}) as Record<string, unknown>;
      const from = placeOf(t);
      const bad = move(principal, t, String(b.board_id ?? ""), b.status as string | undefined);
      if (bad) return bad;
      const undo_id = undoOf(principal, t, "task.move", from, placeOf(t));
      return json({ ...detail(principal, t), undo_id });
    },
    "tasks.setStatus": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const from = t.status;
      const next = String((body as { status?: unknown })?.status ?? "");
      if (next !== t.status) {
        const locked = lockRefusal(principal, t);
        if (locked) return locked;
      }
      const bad = setStatus(principal, t, next);
      if (bad) return bad;
      const undo_id =
        from !== t.status ? undoOf(principal, t, "task.status", from, t.status) : null;
      return json({ ...detail(principal, t), undo_id });
    },
    "tasks.assign": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const seatIds = (body as { seat_ids?: unknown })?.seat_ids;
      if (!Array.isArray(seatIds) || !seatIds.length || !seatIds.every(isUuid))
        return validation("seat_ids: 1–20 seat ids");
      // As the API: assigning only seats already on it is a no-op, lock or not.
      const fresh = (seatIds as string[]).some((s) => !t.assignee_seat_ids.includes(s));
      // SHARED-DES: a shared DE takes work only from its chain and the scopes it serves.
      const outOfReach = fresh
        ? deps.taskRefusal?.(
            principal,
            (seatIds as string[]).filter((s) => !t.assignee_seat_ids.includes(s)),
          )
        : null;
      if (outOfReach) return outOfReach;
      const locked = fresh ? lockRefusal(principal, t) : null;
      if (locked) return locked;
      // A handoff (domain/boards/tasks.ts `handOffTask`): the handing seat must be assigned, the
      // task goes to the other seats and leaves the handing one.
      const handoff = (body as { handoff?: { from_seat_id?: unknown; reason?: unknown } })?.handoff;
      if (handoff) {
        const from = String(handoff.from_seat_id ?? "").toLowerCase();
        if (!isUuid(from)) return validation("handoff.from_seat_id: a seat id");
        const to = [...new Set(seatIds.map((s: string) => s.toLowerCase()))].filter(
          (s) => s !== from,
        );
        if (!to.length)
          return problem(
            409,
            "conflict",
            "Conflict",
            "a handoff names a seat other than the handing one",
          );
        if (!t.assignee_seat_ids.map((s) => s.toLowerCase()).includes(from))
          return problem(
            409,
            "conflict",
            "Conflict",
            "the handing seat is not assigned to this task",
          );
        const bad = assign(principal, t, to);
        if (bad) return bad;
        // As `handOffTask`: the unassignment (its own `task.unassigned`), then the handoff.
        unassign(principal, t, t.assignee_seat_ids.find((x) => x.toLowerCase() === from) ?? from);
        record(principal.company, principal, "task.handed_off", t.id, {
          board_id: t.board_id,
          from_seat_id: from,
          to_seat_ids: to,
          reason:
            typeof handoff.reason === "string" && handoff.reason.trim()
              ? handoff.reason.trim()
              : null,
        });
        return json(detail(principal, t));
      }
      const bad = assign(principal, t, seatIds);
      return bad ?? json(detail(principal, t));
    },
    // COS-MODES: Take back on an `agent_directed` inbox item. The twin runs no agent: it checks
    // the item is the caller's, records `task.taken_back` and answers the task; once only.
    "tasks.takeBack": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const eventId = (body as { event_id?: unknown } | null)?.event_id;
      if (typeof eventId !== "string" || !isUuid(eventId))
        return validation("event_id: an event id");
      const me = membershipOf(principal);
      const item = stateOf(principal.company).inbox.find(
        (i) =>
          i.kind === "agent_directed" &&
          i.subject_id === t.id &&
          i.source_event_id === eventId &&
          i.membership_id === me,
      );
      if (!item || !me) return notFound();
      if (takenBack.has(eventId))
        return problem(409, "conflict", "Conflict", "This was already taken back");
      takenBack.add(eventId);
      record(principal.company, principal, "task.taken_back", t.id, {
        board_id: t.board_id,
        directed_event_id: eventId,
        by_membership_id: me,
        from_seat_ids: [...t.assignee_seat_ids],
        to_seat_id: null,
        restored: [],
      });
      return json(detail(principal, t));
    },
    // AGENT-MENTION: the task's handoffs to the DEs holding its seats. This twin runs nothing:
    // a live DE's handoff follows the task's status (Backlog: not woken; in progress: running;
    // done: done; otherwise queued); retired, paused, in setup or add-on off say why not.
    "tasks.agentWakes": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const on = deps.featureOn(principal, "agents");
      const data = deps
        .desOf(principal.company.id)
        .filter((d) => d.status !== "retired" && t.assignee_seat_ids.includes(d.seat_id))
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .map((d) => {
          const n = d.display_name;
          const why: [string, string] =
            d.status === "paused"
              ? ["paused", `${n} is paused.`]
              : d.status === "not_deployed"
                ? ["not_live", `${n} is not deployed.`]
                : d.status === "onboarding"
                  ? ["not_live", `${n} is still being set up.`]
                  : !on
                    ? ["not_live", "Agents are off for this company."]
                    : t.status === "backlog"
                      ? [
                          "blocked",
                          `In Backlog: ${n} is woken when the task is assigned outside Backlog.`,
                        ]
                      : t.status === "done"
                        ? ["done", `${n} finished.`]
                        : t.status === "in_progress"
                          ? ["running", `${n} is working on it.`]
                          : ["queued", `Handing the task to ${n}.`];
          return {
            membership_id: d.membership_id,
            name: n,
            agent_kind: "digital_employee" as const,
            seat_id: d.seat_id,
            state: why[0] as OperationOutput<"tasks.agentWakes">["data"][number]["state"],
            reason: why[1],
            run_id: null,
            requested_at: null,
          };
        });
      return json({ data });
    },
    "tasks.unassign": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const locked = lockRefusal(principal, t);
      if (locked) return locked;
      unassign(principal, t, params.seat_id ?? "");
      return new Response(null, { status: 204 });
    },
    // TASK-LOCK: "Stop agent and move" — cancels every live agent run on the task.
    "tasks.stopAgent": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const live = liveRunsOn(principal, t.id);
      if (!canStopAgents(principal, t.id))
        return problem(
          403,
          "forbidden",
          "Not allowed",
          "an admin or manager, or the task's owner, stops the agent working on it",
        );
      // The task-level right already decided it (the agent's supervisor need not be the caller),
      // so each twin's run row is ended here, not through `runs.cancel` and its run visibility.
      const at = deps.now().toISOString();
      for (const r of live) {
        const row = r as Record<string, unknown>;
        row.state = "cancelled";
        row.state_reason = "stopped by a person (Stop agent and move)";
        row.ended_at = at;
        row.updated_at = at;
        row.lease_owner = null;
        row.lease_until = null;
        if ("budget_reserved_micros" in row) row.budget_reserved_micros = 0;
      }
      return json({ ...detail(principal, t), undo_id: null });
    },
    "tasks.setField": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const value = (body as { value?: unknown })?.value ?? null;
      const from = t.fields[params.field_id ?? ""] ?? null;
      const bad = setField(principal, t, params.field_id ?? "", value);
      if (bad) return bad;
      const undo_id = sameJson(from, value)
        ? null
        : undoOf(principal, t, "task.field", from, value, params.field_id);
      return json({
        undo_id,
        company_id: t.company_id,
        task_id: t.id,
        field_def_id: params.field_id,
        value,
        ...rowActor(principal),
        created_at: t.created_at,
        updated_at: t.updated_at,
      });
    },
    "tasks.checklist": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      return t ? json({ data: detail(principal, t).checklist_items }) : notFound();
    },
    "tasks.addChecklistItem": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const text = typeof b.text === "string" ? b.text.trim() : "";
      if (!text || text.length > 500) return validation("text must be 1–500 characters");
      let list: Checklist | undefined;
      if (b.checklist_id !== undefined) {
        list = t.checklists.find((l) => l.id === b.checklist_id);
        if (!list) return validation("checklist_id: not a checklist of this task");
      }
      const bad = badAssignee(principal, b.assignee_seat_id);
      if (bad) return bad;
      list ??= listsOf(t)[0] ?? newChecklist(principal, t, "Checklist");
      const item = newItem(principal, t, list.id, text, {
        position: b.position as number | undefined,
        assignee_seat_id: (b.assignee_seat_id as string | null | undefined) ?? null,
        due_at: (b.due_at as string | null | undefined) ?? null,
      });
      touch(principal, t);
      checklistChanged(principal, t, list.id, item.id, "added", false);
      return json(item, 201);
    },
    "tasks.updateChecklistItem": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      const item = t?.checklist_items.find((c) => c.id === params.item_id);
      if (!t || !item) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.text !== undefined) {
        const text = String(b.text).trim();
        if (!text) return validation("text must not be empty");
        item.text = text;
      }
      const bad = badAssignee(principal, b.assignee_seat_id);
      if (bad) return bad;
      // Moving to another checklist of the task puts the item last there, unless a position is given.
      if (b.checklist_id !== undefined && b.checklist_id !== item.checklist_id) {
        const to = t.checklists.find((l) => l.id === b.checklist_id);
        if (!to) return validation("checklist_id: not a checklist of this task");
        item.position = b.position !== undefined ? Number(b.position) : nextItemPos(t, to.id);
        item.checklist_id = to.id;
      } else if (b.position !== undefined) item.position = Number(b.position);
      if (b.done !== undefined) {
        const was = item.done_at !== null;
        if (b.done && !was) {
          item.done_at = stamp();
          item.done_by_seat_id = null;
        } else if (!b.done && was) {
          item.done_at = null;
          item.done_by_seat_id = null;
        }
      }
      if (b.assignee_seat_id !== undefined)
        item.assignee_seat_id = (b.assignee_seat_id as string | null) ?? null;
      if (b.due_at !== undefined) item.due_at = (b.due_at as string | null) ?? null;
      item.updated_at = stamp();
      touch(principal, t);
      checklistChanged(principal, t, item.checklist_id, item.id, "updated", item.done_at !== null);
      return json(item);
    },
    "tasks.deleteChecklistItem": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      const item = t?.checklist_items.find((c) => c.id === params.item_id);
      if (!t || !item) return notFound();
      t.checklist_items = t.checklist_items.filter((c) => c.id !== item.id);
      touch(principal, t);
      checklistChanged(principal, t, item.checklist_id, item.id, "removed", item.done_at !== null);
      return new Response(null, { status: 204 });
    },
    "tasks.checklists": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      return t ? json({ data: listsOf(t).map((l) => withItems(t, l)) }) : notFound();
    },
    "tasks.createChecklist": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const title = b.title === undefined ? "Checklist" : String(b.title).trim();
      if (!title || title.length > 200) return validation("title must be 1–200 characters");
      const texts = ((b.items as string[] | undefined) ?? []).map((x) => String(x).trim());
      if (texts.some((x) => !x || x.length > 500))
        return validation("items must be 1–500 characters each");
      const l = newChecklist(principal, t, title, b.position as number | undefined);
      for (const [i, text] of texts.entries()) newItem(principal, t, l.id, text, { position: i });
      touch(principal, t);
      checklistChanged(principal, t, l.id, null, "list_added", false);
      return json(withItems(t, l), 201);
    },
    "tasks.updateChecklist": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      const l = t?.checklists.find((c) => c.id === params.checklist_id);
      if (!t || !l) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const title = b.title === undefined ? undefined : String(b.title).trim();
      if (title !== undefined && (!title || title.length > 200))
        return validation("title must be 1–200 characters");
      const renamed = title !== undefined && title !== l.title;
      const moved = b.position !== undefined && Number(b.position) !== l.position;
      if (!renamed && !moved) return json(withItems(t, l));
      if (renamed) l.title = title;
      if (moved) l.position = Number(b.position);
      l.updated_at = stamp();
      Object.assign(l, rowActor(principal));
      touch(principal, t);
      checklistChanged(principal, t, l.id, null, renamed ? "list_renamed" : "reordered", false);
      return json(withItems(t, l));
    },
    "tasks.deleteChecklist": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      const l = t?.checklists.find((c) => c.id === params.checklist_id);
      if (!t || !l) return notFound();
      t.checklists = t.checklists.filter((c) => c.id !== l.id);
      t.checklist_items = t.checklist_items.filter((i) => i.checklist_id !== l.id);
      touch(principal, t);
      checklistChanged(principal, t, l.id, null, "list_removed", false);
      return new Response(null, { status: 204 });
    },
    "tasks.reorderChecklist": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      const l = t?.checklists.find((c) => c.id === params.checklist_id);
      if (!t || !l) return notFound();
      const wanted = ((body ?? {}) as { item_ids?: string[] }).item_ids ?? [];
      const current = itemsOf(t, l.id);
      const known = new Set(current.map((i) => i.id));
      if (!wanted.length) return validation("item_ids must not be empty");
      if (new Set(wanted).size !== wanted.length) return validation("item_ids: an id twice");
      const foreign = wanted.find((id) => !known.has(id));
      if (foreign) return validation(`item_ids: ${foreign} is not an item of this checklist`);
      // The given items first, in that order, then the ones left out in their current order.
      const rest = current.filter((i) => !wanted.includes(i.id)).map((i) => i.id);
      const order = [...wanted, ...rest];
      if (order.every((id, i) => current[i]?.id === id)) return json(withItems(t, l));
      const at = stamp();
      for (const [pos, id] of order.entries()) {
        const item = current.find((i) => i.id === id);
        if (item && item.position !== pos) {
          item.position = pos;
          item.updated_at = at;
        }
      }
      touch(principal, t);
      checklistChanged(principal, t, l.id, null, "reordered", false);
      return json(withItems(t, l));
    },
    "tasks.dependencies": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      return t ? json({ data: t.dependencies }) : notFound();
    },
    "tasks.addDependency": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const on = taskIn(principal, b.depends_on_task_id as string | undefined);
      if (!on) return validation("depends_on_task_id: no such task");
      const kind = (b.kind as "hard" | "soft" | undefined) ?? "hard";
      if (t.dependencies.some((d) => d.depends_on_task_id === on.id))
        return problem(409, "conflict", "Conflict", "This dependency already exists.");
      // No cycles: walk what `on` depends on; reaching `t` closes a loop.
      const seen = new Set<string>();
      const stack = [on.id];
      while (stack.length) {
        const id = stack.pop() as string;
        if (id === t.id)
          return problem(
            422,
            "dependency_cycle",
            "The dependency would form a cycle",
            `#${on.number} already waits on #${t.number}.`,
          );
        if (seen.has(id)) continue;
        seen.add(id);
        for (const d of taskIn(principal, id)?.dependencies ?? []) stack.push(d.depends_on_task_id);
      }
      const row = {
        company_id: t.company_id,
        task_id: t.id,
        depends_on_task_id: on.id,
        kind,
        ...rowActor(principal),
        created_at: stamp(),
      };
      t.dependencies.push(row);
      touch(principal, t);
      record(principal.company, principal, "task.dependency_changed", t.id, {
        board_id: t.board_id,
        depends_on_task_id: on.id,
        kind,
        op: "added",
      });
      return json(row, 201);
    },
    "tasks.removeDependency": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      const dep = t.dependencies.find((d) => d.depends_on_task_id === params.item_id);
      if (dep) {
        t.dependencies = t.dependencies.filter((d) => d !== dep);
        touch(principal, t);
        record(principal.company, principal, "task.dependency_changed", t.id, {
          board_id: t.board_id,
          depends_on_task_id: dep.depends_on_task_id,
          kind: dep.kind,
          op: "removed",
        });
      }
      return new Response(null, { status: 204 });
    },
    "tasks.thread": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      const t = taskIn(principal, params.id);
      if (!t) return notFound();
      // Created on first use (a task without messages has no thread row yet).
      if (!t.thread_id) {
        t.thread_id = ids.next(0x5700);
        stateOf(principal.company).threads.set(t.thread_id, t.id);
      }
      return json({ thread_id: t.thread_id });
    },
    "tasks.history": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      const t = stateOf(principal.company).tasks.find((x) => x.id === params.id);
      if (!t || t.deleted) return notFound();
      const rows = stateOf(principal.company)
        .events.filter((e) => e.subject.id === t.id)
        .sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0));
      return json(page(rows, url));
    },
    // M2-08: the CSV task import, with the API's row rules (`@companyos/domain-work/import`).
    // Committed batches are not listed by this twin's `imports.list` (that state is fake.ts's).
    "tasks.import": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      const board = boardIn(principal, params.id);
      if (!board) return notFound();
      if (board.archived_at) return problem(409, "conflict", "Conflict", "the board is archived");
      const form = (body ?? {}) as { file?: string; dry_run?: string; mapping?: string };
      if (typeof form.file !== "string") return validation("file: a CSV part is required");
      let mapping: TaskImportMapping;
      try {
        mapping = JSON.parse(form.mapping ?? "") as TaskImportMapping;
      } catch {
        return validation("mapping: a JSON mapping is required");
      }
      const csv = previewCsv(form.file, 5_000);
      const defs = fieldsOf(principal, board.id) as unknown as FieldDefLike[];
      const problems = mappingProblems(mapping, csv.header, defs);
      if (problems.length > 0) return validation(problems.map((x) => x.message).join("; "));
      if (csv.total === 0) return validation("file: the file has no data rows");
      const dryRun = form.dry_run !== "false";
      const s = stateOf(principal.company);
      const batchId = ids.next(0x9b00);
      type Row = { row: number; status: string; ref?: string; errors?: object[] };
      const rows: Row[] = [];
      const writes: { row: number; input: Record<string, unknown>; client: string }[] = [];
      for (const [i, cells] of csv.rows.entries()) {
        const row = i + 1;
        const r = parseTaskRow(cells, mapping, { statuses: board.status_set, fields: defs });
        if (!r.ok) {
          rows.push({ row, status: "error", errors: r.errors });
          continue;
        }
        const seatIds: string[] = [];
        const missing: string[] = [];
        for (const ref of r.assignees) {
          const seat = seatList(principal).find(
            (x) => x.id === ref || x.title.toLowerCase() === ref.toLowerCase(),
          );
          if (seat) seatIds.push(seat.id);
          else missing.push(ref);
        }
        if (missing.length > 0) {
          rows.push({
            row,
            status: "unmapped",
            errors: missing.map((m) => ({ code: "unmapped", message: `no seat "${m}"` })),
          });
          continue;
        }
        const client = `import:${board.id}:${rowIdentity(cells, mapping)}`;
        const existing = taskIn(principal, s.clientIds.get(client));
        if (existing || writes.some((w) => w.client === client)) {
          rows.push({ row, status: "duplicate", ...(existing ? { ref: existing.id } : {}) });
          continue;
        }
        rows.push({ row, status: "ok" });
        writes.push({
          row,
          client,
          input: { ...r.task, board_id: board.id, assignee_seat_ids: seatIds },
        });
      }
      const failed = rows.some((r) => r.status === "error" || r.status === "unmapped");
      if (!dryRun && failed)
        return problem(422, "validation_failed", "Validation failed", "fix every row first");
      if (!dryRun)
        for (const w of writes) {
          const made = createTask(principal, w.input);
          if (made instanceof Response) return made;
          made.task.origin = "import";
          made.task.origin_ref = { import_batch_id: batchId };
          s.clientIds.set(w.client, made.task.id);
          const out = rows.find((r) => r.row === w.row);
          if (out) out.ref = made.task.id;
        }
      const counts: Record<string, number> = {};
      for (const r of rows) counts[r.status] = (counts[r.status] ?? 0) + 1;
      return json({ batch_id: batchId, resource: "tasks", dry_run: dryRun, rows, counts });
    },
    "tasks.bulk": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const b = (body ?? {}) as {
        task_ids?: unknown;
        action?: { kind?: string } & Record<string, unknown>;
      };
      if (
        !Array.isArray(b.task_ids) ||
        !b.task_ids.length ||
        b.task_ids.length > 200 ||
        !b.task_ids.every(isUuid)
      )
        return validation("task_ids: 1–200 task ids");
      const action = b.action;
      if (!action || !["status", "assign", "move", "field", "delete"].includes(String(action.kind)))
        return validation("action.kind: status, assign, move, field or delete");
      const results = (b.task_ids as string[]).map((id) => {
        const t = taskIn(principal, id);
        if (!t) return { task_id: id, ok: false, code: "not_found" };
        // TASK-LOCK, as each single route: a status change, a new seat or a move of a task an
        // agent works on is refused per item (`task_locked`), the rest of the batch goes on.
        const locks =
          (action.kind === "move" && String(action.board_id ?? "") !== t.board_id) ||
          (action.kind === "status" && String(action.status ?? "") !== t.status) ||
          (action.kind === "assign" &&
            ((action.seat_ids as string[] | undefined) ?? []).some(
              (s) => !t.assignee_seat_ids.includes(s),
            ));
        if (locks && lockRefusal(principal, t))
          return { task_id: id, ok: false, code: "task_locked" };
        let bad: Response | null = null;
        if (action.kind === "status") bad = setStatus(principal, t, String(action.status ?? ""));
        else if (action.kind === "assign")
          bad = assign(principal, t, (action.seat_ids as string[] | undefined) ?? []);
        else if (action.kind === "move") bad = move(principal, t, String(action.board_id ?? ""));
        else if (action.kind === "field")
          bad = setField(principal, t, String(action.field_id ?? ""), action.value ?? null);
        else remove(principal, t);
        if (!bad) return { task_id: id, ok: true, code: null };
        const code =
          bad.status === 404
            ? "not_found"
            : bad.status === 409 && action.kind === "status"
              ? "dependency_blocked"
              : bad.status === 422 && action.kind === "status"
                ? "status_invalid"
                : "validation_failed";
        return { task_id: id, ok: false, code };
      });
      return json({ results });
    },
    "tasks.search": ({ principal, url, body }) => {
      if (!principal) return notFound();
      if (!reader(principal)) return json(page([], url));
      const b = (body ?? {}) as {
        query?: string;
        board_ids?: string[];
        filters?: View["filter"];
        include?: string[];
        cursor?: string;
        limit?: number;
      };
      const q = (b.query ?? "").trim().toLowerCase();
      const s = stateOf(principal.company);
      const rows = s.tasks
        .filter((t) => !t.deleted && (!b.board_ids?.length || b.board_ids.includes(t.board_id)))
        .filter(
          (t) =>
            !q ||
            t.title.toLowerCase().includes(q) ||
            `#${t.number}` === q ||
            String(t.number) === q,
        )
        .map((t) => card(principal, t))
        .filter((t) => {
          if (!b.filters) return true;
          const board = boardIn(principal, t.board_id);
          return board
            ? applyView(
                [t],
                { filter: b.filters, sort: [], group_by: null },
                contextOf(principal, board),
              )[0]?.tasks.length === 1
            : false;
        });
      const at = new URL(url);
      if (b.cursor) at.searchParams.set("cursor", b.cursor);
      if (b.limit) at.searchParams.set("limit", String(b.limit));
      // T5-TASK-SEARCH: the in-memory twin keeps no bodies, observations, links or status
      // history here; a hit asked for them carries honest empties.
      const include = new Set(b.include ?? []);
      const withExtras = include.size
        ? rows.map((t) => ({
            ...t,
            ...(include.has("brief") ? { brief: null } : {}),
            ...(include.has("numbers") ? { numbers: [] } : {}),
            ...(include.has("links") ? { links: [] } : {}),
            ...(include.has("last_status_change") ? { last_status_change: null } : {}),
          }))
        : rows;
      return json(page(withExtras, at));
    },
    "my.tasks": ({ principal, url }) => {
      if (!principal) return notFound();
      const m = membershipOf(principal);
      // The caller's seats: the seats their membership holds (the seeded supervisors).
      const mine = new Set(
        deps
          .desOf(principal.company.id)
          .filter((d) => d.supervisor.membership_id === m)
          .map((d) => d.supervisor.seat_id),
      );
      const includeDone = url.searchParams.get("include_done") === "true";
      const before = url.searchParams.get("due_before");
      const rows = stateOf(principal.company)
        .tasks.filter((t) => !t.deleted && t.assignee_seat_ids.some((x) => mine.has(x)))
        .filter(
          (t) =>
            includeDone ||
            !isDoneStatus(boardIn(principal, t.board_id)?.status_set ?? [], t.status),
        )
        .filter((t) => !before || (t.due_at !== null && t.due_at < before))
        .sort(
          (a, b) => (a.due_at ?? "9999").localeCompare(b.due_at ?? "9999") || a.number - b.number,
        )
        .map((t) => card(principal, t));
      return json(page(rows, url));
    },

    // Follows (M2-04's routes; the task drawer's Follow).
    "follows.put": ({ principal, body }) => {
      if (!reader(principal)) return notFound();
      const m = membershipOf(principal);
      if (!m) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      if (
        !["channel", "topic", "thread", "task", "board"].includes(String(b.subject_kind)) ||
        !isUuid(b.subject_id)
      )
        return validation("subject_kind and subject_id are required");
      if (b.subject_kind === "task" && !taskIn(principal, b.subject_id)) return notFound();
      if (b.subject_kind === "board" && !boardIn(principal, b.subject_id)) return notFound();
      const level =
        (b.level as Follow["level"] | undefined) ??
        (b.subject_kind === "board" ? "mentions" : "all");
      return json(
        follow(principal, m, b.subject_kind as Follow["subject_kind"], b.subject_id, level),
      );
    },
    "follows.delete": ({ principal, url }) => {
      if (!reader(principal)) return notFound();
      const m = membershipOf(principal);
      const s = stateOf(principal.company);
      const kind = url.searchParams.get("subject_kind");
      const id = url.searchParams.get("subject_id");
      s.follows = s.follows.filter(
        (f) => !(f.membership_id === m && f.subject_kind === kind && f.subject_id === id),
      );
      return new Response(null, { status: 204 });
    },

    // Inbox (M2-04's routes): one inbox per person per company; DEs and operators have none.
    "inbox.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const m = membershipOf(principal);
      const filter = url.searchParams.get("filter");
      if (filter && !INBOX_FILTERS.includes(filter))
        return validation(`filter: ${INBOX_FILTERS.join(", ")}`);
      const now = stamp();
      const rows = stateOf(principal.company)
        .inbox.filter((i) => i.membership_id === m && inboxMatches(i, filter, now))
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
      return json(page(rows, url));
    },
    "inbox.read": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const item = stateOf(principal.company).inbox.find(
        (i) => i.id === params.id && i.membership_id === membershipOf(principal),
      );
      if (!item) return notFound();
      const read = (body as { read?: boolean } | undefined)?.read ?? true;
      item.read_at = read ? (item.read_at ?? stamp()) : null;
      return json(item);
    },
    "inbox.done": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const item = stateOf(principal.company).inbox.find(
        (i) => i.id === params.id && i.membership_id === membershipOf(principal),
      );
      if (!item) return notFound();
      const done = (body as { done?: boolean } | undefined)?.done ?? true;
      item.done_at = done ? (item.done_at ?? stamp()) : null;
      if (done) item.read_at = item.read_at ?? item.done_at;
      return json(item);
    },
    "inbox.readAll": ({ principal, body }) => {
      if (!principal) return notFound();
      const m = membershipOf(principal);
      const filter = (body as { filter?: string } | undefined)?.filter;
      let count = 0;
      const now = stamp();
      for (const i of stateOf(principal.company).inbox) {
        if (i.membership_id !== m || i.read_at || !inboxMatches(i, filter, now)) continue;
        i.read_at = now;
        count += 1;
      }
      return json({ count });
    },
    "inbox.snooze": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const item = stateOf(principal.company).inbox.find(
        (i) => i.id === params.id && i.membership_id === membershipOf(principal),
      );
      if (!item) return notFound();
      const until = (body as { until?: string | null } | undefined)?.until ?? null;
      if (until !== null && until <= stamp()) return validation("until must be in the future");
      item.snoozed_until = until;
      return json(item);
    },
    // Search (M2-04): this twin's tasks (Chat's twin adds its messages and tasks; fake.ts).
    "search.query": ({ principal, url }) => {
      if (!principal) return notFound();
      const q = url.searchParams.get("q") ?? "";
      // CHAT-SWITCH-A: any filter limits the search to messages (Chat's twin answers those).
      const filtered = ["from", "in", "has", "is", "before", "after"].some((k) =>
        url.searchParams.get(k),
      );
      if (filtered) return json(page([], url));
      if (!q.trim()) return validation("a search needs words or a filter");
      if (url.searchParams.get("kind") === "message" || !reader(principal))
        return json(page([], url));
      const hits = stateOf(principal.company)
        .tasks.filter((t) => textMatches(q, t.title) && taskIn(principal, t.id))
        .map((t) => ({
          kind: "task" as const,
          id: t.id,
          title: t.title,
          snippet: t.title.slice(0, 200),
          created_at: t.created_at,
          board_id: t.board_id,
          task_number: t.number,
          thread_id: null,
          channel_id: null,
          topic_id: null,
          author_membership_id: null,
        }))
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      return json(page(hits, url));
    },

    // Task threads (M2-02's message routes, served for task threads only here).
    "threads.get": ({ principal, params }) => {
      if (!reader(principal)) return notFound();
      if (!deps.featureOn(principal, "chat")) return chatOff();
      const taskId = stateOf(principal.company).threads.get(params.id ?? "");
      const t = taskId ? taskIn(principal, taskId) : null;
      if (!t) return notFound();
      const msgs = stateOf(principal.company).messages.get(params.id ?? "") ?? [];
      return json({
        company_id: t.company_id,
        id: params.id,
        kind: "task",
        subject_id: t.id,
        last_message_at: msgs.at(-1)?.created_at ?? null,
        message_count: msgs.length,
        created_at: t.created_at,
        updated_at: msgs.at(-1)?.created_at ?? t.created_at,
      });
    },
    "messages.list": ({ principal, params, url }) => {
      if (!reader(principal)) return notFound();
      if (!deps.featureOn(principal, "chat")) return chatOff();
      if (!stateOf(principal.company).threads.has(params.id ?? "")) return notFound();
      const msgs = stateOf(principal.company).messages.get(params.id ?? "") ?? [];
      const limit = Math.min(200, Number(url.searchParams.get("limit") ?? 50) || 50);
      return json({
        data: msgs.slice(-limit),
        next_cursor: msgs.length > limit ? String(msgs.length - limit) : null,
        prev_cursor: null,
      });
    },
    "messages.create": ({ principal, params, body }) => {
      if (!reader(principal)) return notFound();
      if (!deps.featureOn(principal, "chat")) return chatOff();
      const s = stateOf(principal.company);
      if (!s.threads.has(params.id ?? "")) return notFound();
      const b = (body ?? {}) as {
        client_id?: string;
        body_markdown?: string;
        body?: unknown;
        mentions?: string[];
      };
      if (!b.client_id) return validation("client_id is required");
      if ((b.body === undefined) === (b.body_markdown === undefined))
        return validation("exactly one of body and body_markdown");
      const list = s.messages.get(params.id ?? "") ?? [];
      const again = list.find((m) => m.client_id === b.client_id);
      if (again) return json(again, 201);
      const text = (b.body_markdown ?? "").trim();
      if (!text) return validation("body_markdown must not be empty");
      const m = membershipOf(principal);
      const row = messageRow(principal.company.id, params.id ?? "", stamp(), text, m, {
        kind: m ? "person" : "agency_user",
        seat: null,
        mentions: b.mentions ?? [],
        clientId: b.client_id,
      });
      list.push(row);
      s.messages.set(params.id ?? "", list);
      return json(row, 201);
    },
    // wp/ACTIONS: a task comment's edit (its author, within 15 minutes), delete (its author) and
    // pin (anyone who may post), as the chat twin's for channel threads; a 404 here falls through
    // to that twin (fake.ts `twinRoutes`).
    "messages.update": ({ principal, params, body }) => {
      const found = taskMessage(principal, params);
      if (found instanceof Response) return found;
      if (!principal || found.author_membership_id !== membershipOf(principal))
        return problem(403, "forbidden", "Forbidden", "Only the author edits a message.");
      if (deps.now().getTime() - new Date(found.created_at).getTime() > 15 * 60_000)
        return problem(
          409,
          "edit_window_closed" as never,
          "Edit window closed",
          "Messages can be edited for 15 minutes after posting.",
        );
      const text = String((body as { body_markdown?: string } | null)?.body_markdown ?? "").trim();
      if (!text) return validation("a message needs text");
      found.body = text.split("\n").map((line) => ({ type: "paragraph", content: line })) as never;
      found.body_text = text;
      found.edited_at = stamp();
      return json(found);
    },
    "messages.delete": ({ principal, params }) => {
      const found = taskMessage(principal, params);
      if (found instanceof Response) return found;
      if (!principal || found.author_membership_id !== membershipOf(principal))
        return problem(403, "forbidden", "Forbidden", "Only the author deletes a message.");
      found.deleted_at = stamp();
      found.body = [] as never;
      found.body_text = "";
      return new Response(null, { status: 204 });
    },
    "messages.pin": ({ principal, params, body }) => {
      const found = taskMessage(principal, params);
      if (found instanceof Response) return found;
      found.pinned_at = (body as { pinned?: boolean } | null)?.pinned
        ? (found.pinned_at ?? stamp())
        : null;
      return json(found);
    },
    // Parity P1: undo a reference write (see `undoOf` above).
    // AGENT-GUARDRAILS: the caller's own live records on the task (the fake has no agent writers
    // and no write events to sit them on, so `event_id` is null).
    "undo.list": (ctx) => {
      const { principal, url } = ctx;
      if (!reader(principal)) return notFound();
      const taskId = url.searchParams.get("subject_id") ?? "";
      if (!taskIn(principal, taskId)) return notFound();
      const now = deps.now().getTime();
      const items = [...undos.values()]
        .filter(
          (u) =>
            u.companyId === principal.company.id &&
            u.taskId === taskId &&
            u.membership === membershipOf(principal) &&
            !u.usedAt &&
            now < u.at + 15 * 60_000,
        )
        .map((u) => ({
          undo_id: u.id,
          kind: u.kind,
          event_id: null,
          actor: { kind: "person", membership_id: u.membership, name: null },
          created_at: new Date(u.at).toISOString(),
          expires_at: new Date(u.at + 15 * 60_000).toISOString(),
        }));
      return json({ items });
    },
    "undo.apply": (ctx) => {
      const { principal, params } = ctx;
      if (!reader(principal)) return notFound();
      const u = undos.get(params.id ?? "");
      if (!u || u.companyId !== principal.company.id) return notFound();
      if (u.membership !== membershipOf(principal))
        return problem(403, "undo_not_allowed", "Only who made the change can undo it");
      const t = taskIn(principal, u.taskId);
      if (!t) return notFound();
      const answer = (replay: boolean) =>
        json({
          undo_id: u.id,
          kind: u.kind,
          subject: { kind: "task", id: t.id },
          used_at: u.usedAt,
          replay,
          task: detail(principal, t),
        });
      if (u.usedAt) return answer(true);
      if (deps.now().getTime() >= u.at + 15 * 60_000)
        return problem(410, "undo_expired", "This change can no longer be undone");
      if (!sameJson(currentOf(t, u), u.to))
        return problem(
          409,
          "undo_stale",
          "The subject changed after this; it was not undone",
          `Someone changed the ${WHAT[u.kind]} after this`,
        );
      let bad: Response | null = null;
      if (u.kind === "task.status") bad = setStatus(principal, t, String(u.from));
      else if (u.kind === "task.field")
        bad = setField(principal, t, u.fieldId ?? "", u.from ?? null);
      else if (u.kind === "task.move") {
        const back = u.from as { board_id: string; status: string };
        bad = move(principal, t, back.board_id, back.status);
      } else {
        const r = handlers["tasks.update"]?.({
          ...ctx,
          params: { ...params, id: t.id },
          body: u.from,
          headers: new Headers({ "if-match": String(t.version) }),
        });
        if (r && r.status >= 400) bad = r;
      }
      if (bad) return bad;
      record(principal.company, principal, "task.undone", t.id, {
        undo_id: u.id,
        kind: u.kind,
        undone_event_id: null,
        board_id: t.board_id,
      });
      u.usedAt = stamp();
      return answer(false);
    },
  };
  return handlers;

  function chatOff() {
    return new Response(
      JSON.stringify({
        type: "https://companyos.app/errors/feature_disabled",
        title: "Feature not enabled",
        status: 403,
        code: "feature_disabled",
        feature: "chat",
        request_id: "fake-work",
        detail: "The chat add-on is not enabled for this company.",
      }),
      { status: 403, headers: { "content-type": "application/problem+json" } },
    );
  }
}
